/**
 * The Lillebjörn — Size Guide & Fit Finder page section.
 * Native Shopify Web Component (no build step, no framework), reading real
 * product/variant JSON emitted by sections/lillebjorn-size-guide.liquid —
 * same pattern as lillebjorn-fit-finder.js's drawer, adapted for a
 * full-page (not drawer/modal) layout.
 *
 * Sizing/breed/guardrail/width-matrix logic itself lives in the shared,
 * framework-free shopify/assets/lillebjorn-fit-logic.js (window.LillebjornFitLogic,
 * loaded before this file) — copied out of the live drawer so this page
 * and the drawer share one source of truth for those rules. Everything in
 * THIS file is page-rendering, Shopify variant/kit-photo resolution, and
 * cart — concerns the shared engine deliberately has no knowledge of.
 */
(function () {
  "use strict";

  if (customElements.get("lillebjorn-size-guide")) return;

  var FL = window.LillebjornFitLogic;
  if (!FL) {
    console.error("lillebjorn-size-guide: window.LillebjornFitLogic is missing — load lillebjorn-fit-logic.js first.");
    return;
  }

  /* ============================================================
     Money formatting (mirrors lillebjorn-fit-finder.js's money()/
     MONEY_FORMATS exactly, so both surfaces render the shop's real
     money_format identically) — cents in, locale-correct string out.
     ============================================================ */
  function formatNumber(value, decimals, thousandsSep, decimalSep) {
    var sign = value < 0 ? "-" : "";
    var fixed = Math.abs(value).toFixed(decimals);
    var parts = fixed.split(".");
    var withThousands = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
    return sign + withThousands + (parts[1] ? decimalSep + parts[1] : "");
  }
  var MONEY_FORMATS = {
    amount: { decimals: 2, thousands: ",", decimal: "." },
    amount_no_decimals: { decimals: 0, thousands: ",", decimal: "." },
    amount_with_comma_separator: { decimals: 2, thousands: ".", decimal: "," },
    amount_no_decimals_with_comma_separator: { decimals: 0, thousands: ".", decimal: "," },
    amount_with_space_separator: { decimals: 2, thousands: " ", decimal: "," },
    amount_no_decimals_with_space_separator: { decimals: 0, thousands: " ", decimal: "," },
    // Apostrophe grouping (CHF and other de-CH style markets) — see the same
    // addition in lillebjorn-fitting-room.js. Without these the regex below
    // matched nothing and the raw "{{amount_with_apostrophe_separator}}" token
    // rendered verbatim in place of every price.
    amount_with_apostrophe_separator: { decimals: 2, thousands: "'", decimal: "." },
    amount_no_decimals_with_apostrophe_separator: { decimals: 0, thousands: "'", decimal: "." }
  };
  function money(cents, format) {
    if (typeof cents !== "number" || isNaN(cents)) return "";
    var value = cents / 100;
    return (format || "€{{amount}}").replace(
      /\{\{\s*(amount_no_decimals_with_comma_separator|amount_no_decimals_with_space_separator|amount_no_decimals_with_apostrophe_separator|amount_with_comma_separator|amount_with_space_separator|amount_with_apostrophe_separator|amount_no_decimals|amount)\s*\}\}/g,
      function (match, token) {
        var spec = MONEY_FORMATS[token];
        return formatNumber(value, spec.decimals, spec.thousands, spec.decimal);
      }
    );
  }

  /* ============================================================
     Real Shopify variant resolution
     ============================================================ */
  function widthMatches(optionValue, wantWidth) {
    if (!wantWidth || !optionValue) return true;
    var escaped = wantWidth.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp("^" + escaped + "\\b", "i").test(String(optionValue).trim());
  }
  function resolveVariant(productData, want) {
    if (!productData || !productData.variants || !productData.variants.length) return null;
    var pool = productData.variants.filter(function (v) { return v.available !== false; });
    if (!pool.length) return null;
    var sizeOk = function (v) { return !want.size || !v.options.size || v.options.size === want.size; };
    var widthOk = function (v) { return !want.width || !v.options.width || widthMatches(v.options.width, want.width); };
    var found = pool.filter(function (v) { return sizeOk(v) && widthOk(v); });
    return found[0] || null;
  }
  // Mirrors lillebjorn-fit-finder.js's own productCarriesWidthData/
  // productHasWidth exactly — a family the anatomical matrix (FL.getAllowedWidths)
  // allows is still only offered at a width this EXACT mapped product
  // actually stocks. Without this check, resolveFamilyOption below would
  // trust FL.pickPrimaryWidth's guess blindly: e.g. Aeris Classic ships
  // 3cm only, but pickPrimaryWidth picks "2cm" for any small (XXS/XS/S)
  // standard-category dog — resolveVariant then finds no matching variant
  // and the whole family silently vanishes, even though it's in stock.
  function productCarriesWidthData(product) {
    return !!(product && product.variants && product.variants.some(function (v) {
      return v.options && v.options.width;
    }));
  }
  function productHasWidth(product, width) {
    if (!width || !product || !product.variants) return true;
    return product.variants.some(function (v) {
      return v.options && v.options.width && widthMatches(v.options.width, width);
    });
  }
  function productFor(productMap, familyKey, colorKey) {
    return (productMap[familyKey] && productMap[familyKey][colorKey]) || null;
  }

  // "Aeris"/"Vero"/"Harness" — the design-line identifier a family's kit
  // photos are organized under (see lillebjorn-kit-collection-scan.liquid).
  // Derived from the familyKey string itself, never a second hand-
  // maintained table, so it can't drift from PRODUCT_FAMILIES' own keys.
  function designIdentifierForFamily(familyKey) {
    var base = String(familyKey).split("-")[0];
    return base.charAt(0).toUpperCase() + base.slice(1);
  }
  function familyLabel(familyKey) {
    var isWide = familyKey.indexOf("wide") !== -1;
    return designIdentifierForFamily(familyKey) + " " + (isWide ? "Wide" : "Classic");
  }
  // One-line leather-line descriptor for the Collar Style picker — keyed
  // by design line (Aeris/Vero), not by Classic/Wide, since the trait
  // (perforated vs. smooth) is a property of the leather line itself and
  // applies the same way to both widths of that line.
  var FAMILY_STYLE_DESCRIPTOR = { Aeris: 'Perforated & Breathable', Vero: 'Smooth & Timeless' };
  function familyStyleDescriptor(familyKey) {
    return FAMILY_STYLE_DESCRIPTOR[designIdentifierForFamily(familyKey)] || '';
  }

  // Real pre-composed Walk Kit/Complete Bundle photography — mirrors the
  // drawer's resolveKitAsset exactly (same lookup shape, same tierKey/
  // design/color/style/width levels, same "_" fallback-width rule),
  // without the drawer's own verbose diagnostics (which are specific to
  // debugging the live merchant catalog, not needed to duplicate here).
  // Returns { url, title, productUrl } or null — never a bare url string.
  function resolveKitAsset(productMap, tierKey, designId, colorKey, leashStyle, widthKey) {
    var kitImagery = productMap["kit-imagery"];
    if (!kitImagery || (tierKey !== "walkkit" && tierKey !== "bundle")) return null;
    var byDesign = kitImagery[tierKey] && kitImagery[tierKey][designId];
    var byColor = byDesign && byDesign[colorKey];
    var byStyle = byColor && byColor[leashStyle];
    var atWidth = byStyle && byStyle[widthKey || "_"];
    var atFallback = byStyle && byStyle["_"];
    if (atWidth && atWidth.url) return atWidth;
    if (atFallback && atFallback.url) return atFallback;
    return null;
  }

  /* ============================================================
     Decorative/marketing content specific to this page
     ============================================================ */
  // headline leads with the BENEFIT (what the shopper actually cares
  // about — comfort, confidence, control), desc backs it up with the WHY
  // one level down. inches are the client's own rounded, marketing-facing
  // values (0.75"/1.2"/1.75") — not a recomputed conversion — so they stay
  // exactly as specified rather than drifting to 0.79"/1.18"/1.77".
  var WIDTHS = [
    { cm: '2 cm', inches: '0.75"', mm: 20, headline: 'Featherweight Comfort', desc: 'Designed for delicate necks and small breeds. Minimal weight, pure comfort.', weightRange: 'Typically under 8 kg' },
    { cm: '3 cm', inches: '1.2"', mm: 30, headline: 'Everyday Balance', desc: 'The ideal daily ratio of strength and ergonomics for medium dogs.', weightRange: 'Typically 8–20 kg' },
    { cm: '4.5 cm', inches: '1.75"', mm: 45, headline: 'Maximum Support', desc: 'Spreads tension across a wider surface area to protect heavy pullers and larger necks.', weightRange: 'Typically 20 kg+' },
  ];
  function esc(str) { return FL.escapeHtml(str); }

  var BREEDS_BY_COLLAR_SIZE = {};
  var BREEDS_BY_HARNESS_SIZE = {};
  FL.COLLAR_SIZES.forEach(function (s) { BREEDS_BY_COLLAR_SIZE[s] = []; });
  FL.HARNESS_SIZES.forEach(function (s) { BREEDS_BY_HARNESS_SIZE[s] = []; });
  FL.BREED_NAMES.forEach(function (name) {
    var profile = FL.resolveBreedProfile(name);
    var collarLetter = FL.sizeFromValue(profile.neckDefault, FL.NECK_NODES, FL.COLLAR_SIZES);
    var harnessLetter = FL.sizeFromValue(profile.chestDefault, FL.CHEST_NODES, FL.HARNESS_SIZES);
    var entry = { name: name, curated: !!FL.CURATED_BREEDS[name.toLowerCase()] };
    BREEDS_BY_COLLAR_SIZE[collarLetter].push(entry);
    BREEDS_BY_HARNESS_SIZE[harnessLetter].push(entry);
  });
  var BREEDS_DISPLAY_LIMIT = 5;
  // Mobile accordion rows are already gated behind a tap, but the full
  // curated+archetype breed list for a size can still run to a dozen+
  // names — shown in full it pushed the expanded row height well past a
  // screen, making the row above scroll out of view the moment a shopper
  // opened it. Truncated to the same "+ more" popover pattern as desktop,
  // just a shorter cut (3, not 5) to match the narrower column it renders in.
  var BREEDS_DISPLAY_LIMIT_MOBILE = 3;
  function topBreedsForSize(bucket, limit) {
    if (bucket.length === 0) return { shown: '—', extra: [] };
    var n = limit || BREEDS_DISPLAY_LIMIT;
    var sorted = bucket.slice().sort(function (a, b) { return (b.curated ? 1 : 0) - (a.curated ? 1 : 0); });
    var shown = sorted.slice(0, n).map(function (b) { return b.name; });
    var extra = sorted.slice(n).map(function (b) { return b.name; });
    return { shown: shown.join(', '), extra: extra };
  }

  function round2(n) { return Math.round(n * 100) / 100; }
  function cmToIn(cm) { return Math.round(cm / 2.54 * 10) / 10; }
  // Quarter-inch stepper clicks (12.25, 12.75…) need 2 decimals to land
  // exactly — cmToIn's 1-decimal rounding would blur them (13.75 -> 13.7
  // or 13.8). Only used for the stepper's own input display; cmToIn stays
  // 1-decimal everywhere else (size-table boundary labels).
  function cmToInPrecise(cm) { return Math.round(cm / 2.54 * 100) / 100; }
  // Exact conversion — NO Math.round() to a whole cm. Storing a typed inch
  // value as a rounded-to-the-nearest-centimeter integer was the root
  // cause of the "jump" bug: 14.4in was silently forced to 37cm (14.57in
  // once converted back), corrupting both the redisplayed value and which
  // size it matched. cm is a precise floating-point equivalent of exactly
  // what the shopper typed; matching against it happens in
  // lillebjorn-fit-logic.js's boundary-tolerant sizeFromValue/boundaryTie.
  function inToCm(inches) { return inches * 2.54; }

  function LillebjornSizeGuide() {
    return Reflect.construct(HTMLElement, [], LillebjornSizeGuide);
  }
  Object.setPrototypeOf(LillebjornSizeGuide.prototype, HTMLElement.prototype);
  Object.setPrototypeOf(LillebjornSizeGuide, HTMLElement);

  LillebjornSizeGuide.prototype.connectedCallback = function () {
    // Document-scoped listeners are re-attached on every connect (not just
    // the first) — see _wireDocumentListeners — so a disconnect+reconnect
    // of this SAME node (a DOM move, not a destroy) never permanently loses
    // the outside-click/scroll handlers the way a plain early-return would.
    if (this._lbsgInitialized) { this._wireDocumentListeners(); return; }
    this._lbsgInitialized = true;

    this.classList.add("lb-sg");

    var dataScript = this.querySelector('script[data-lb-sg-products]');
    this.productMap = {};
    try {
      this.productMap = dataScript ? JSON.parse(dataScript.textContent) : {};
    } catch (e) {
      console.error("lillebjorn-size-guide: could not parse product JSON", e);
    }

    this.supportEmail = this.getAttribute("data-support-email") || "support@thelillebjorn.com";
    this.moneyFormat = this.getAttribute("data-money-format") || "€{{amount}}";
    this.neckIllustration = this.getAttribute("data-neck-illustration") || "";
    this.chestIllustration = this.getAttribute("data-chest-illustration") || "";

    var discountPercent = parseFloat(this.getAttribute("data-bundle-discount-percent"));
    this.bundleDiscountRate = (!isNaN(discountPercent) && discountPercent >= 0 && discountPercent <= 100)
      ? discountPercent / 100
      : FL.UPSELL_DISCOUNT_RATE_DEFAULT;

    // Trust signals — every one of these degrades to "omit" rather than a
    // fabricated placeholder when the merchant hasn't configured/populated
    // real data. See lillebjorn-size-guide.liquid for exactly where each
    // comes from (Judge.me metafields / section settings).
    var reviewRating = parseFloat(this.getAttribute("data-review-rating"));
    var reviewCount = parseInt(this.getAttribute("data-review-count"), 10);
    this.reviewRating = (!isNaN(reviewRating) && !isNaN(reviewCount) && reviewCount > 0) ? reviewRating : null;
    this.reviewCount = this.reviewRating != null ? reviewCount : null;
    this.leadTimeText = this.getAttribute("data-lead-time-text") || "";
    this.exchangeDetailsText = this.getAttribute("data-exchange-details-text") || "";

    // Trimmed AND validated as a real URL-shaped string, not just
    // "attribute exists" — an unconfigured image_picker setting renders
    // this attribute via `| image_url: width: 60` in the Liquid section,
    // and Shopify's own filter behavior on a blank/nil image setting is
    // not guaranteed to come back as a clean empty string across every
    // theme/runtime (whitespace, or a non-URL placeholder value, would
    // both previously read as truthy here and wrongly commit this color to
    // the background-image branch below instead of its real hex fallback
    // — a broken/blank image renders as nothing, not the intended solid
    // swatch color). Falls back to the always-correct FL.LEATHERS hex
    // fallback in fitDetailPanel for anything that doesn't look like an
    // actual URL.
    this.swatchTextures = {};
    ["black", "red", "green", "blue", "brown"].forEach(function (c) {
      var url = (this.getAttribute("data-swatch-texture-" + c) || "").trim();
      if (url && /^(https?:)?\/\//.test(url)) this.swatchTextures[c] = url;
    }, this);

    this.state = {
      unit: 'cm', breedName: '', breedQuery: '', showBreedList: false, breedListClosing: false,
      ageProfile: 'adult', bodyBuild: null,
      collarTab: 'classic', view: 'all',
      neck: null, chest: null,
      neckBoundaryChoice: null, chestBoundaryChoice: null,
      wizardColor: 'Black', bundle: 'walkkit', leashStyle: FL.LEASH_STYLE_DEFAULT,
      selectedCollarFamily: null,
      exchangeDetailsOpen: false,
      openBreedsPopover: null,
      // Mobile accordion (<=680px) row open/closed state, keyed by
      // "<kind>-<size>" (e.g. "collar-M"). Absent from this map = default
      // behavior (open only if it's the matched row); present = the
      // shopper explicitly toggled it, overriding the default either way.
      // Rows are independent — opening one never closes another, so a
      // shopper on a size boundary can compare two adjacent breed lists
      // side by side without losing their matched row.
      mobileRowOpen: {},
      cartStatus: null, // null | 'adding' | 'added' | 'error'
    };
    this._activeFocusId = null;
    this._debounceTimers = {};
    // Buffers the literal characters the shopper is currently typing into
    // the neck/chest fields, keyed by 'neck'/'chest'. render() reads this
    // (while that field has focus) instead of the derived/rounded display
    // value — see measureBlock — so a full re-render mid-type never
    // overwrites a partial value like "10." or "10.5" being composed.
    this._rawMeasureText = { neck: null, chest: null };

    this.render();
    this._wireEvents();
    this._wireDocumentListeners();
    // After the first render, so the observer watches a laid-out element
    // with real height rather than an empty one that can never satisfy a
    // 0.25 intersection threshold.
    this._setupViewObserver();
  };

  // Removes the two document-scoped listeners wired below — element-scoped
  // delegation (click/input/change/focusout/mousedown/focusin, all bound to
  // `this`) needs no explicit cleanup; it's garbage-collected with the node
  // itself. Only `document`-level listeners outlive a detached node, so
  // only those need an explicit teardown here.
  LillebjornSizeGuide.prototype.disconnectedCallback = function () {
    // Torn down unconditionally (not behind the _docListenersWired guard
    // below) — it's an element-scoped observer, but one holding a strong
    // reference to a now-detached node, and it must not survive to fire an
    // entry event if this node is later reconnected somewhere off-screen.
    if (this._viewObserver) {
      this._viewObserver.disconnect();
      this._viewObserver = null;
    }
    if (!this._docListenersWired) return;
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('scroll', this._onDocScroll, true);
    this._docListenersWired = false;
  };

  var proto = LillebjornSizeGuide.prototype;

  proto.setState = function (patch) {
    Object.assign(this.state, patch);
    this.render();
  };

  proto.currentBreedProfile = function () {
    var profile = FL.resolveBreedProfile(this.state.breedName);
    if (this.state.bodyBuild && FL.BUILD_TO_CATEGORY[this.state.bodyBuild]) {
      profile = Object.assign({}, profile, { category: FL.BUILD_TO_CATEGORY[this.state.bodyBuild], build: this.state.bodyBuild });
    }
    return profile;
  };

  proto.applyBreedSelection = function (name) {
    var profile = FL.resolveBreedProfile(name);
    var sizes = FL.applyAgeProfile(profile, this.state.ageProfile);
    this.setState({
      breedName: name, breedQuery: name, showBreedList: false,
      bodyBuild: profile.build,
      neck: sizes.neckSize, chest: sizes.chestSize,
      neckBoundaryChoice: null, chestBoundaryChoice: null,
      // Sighthounds are recommended Wide 4.5cm (see RECOMMENDATION_RULES.sighthound
      // in the shared engine) — switch the visible Classic/Wide sub-tab to
      // match on every pick, not just once, so the table and illustration
      // shown always match this breed's actual recommendation, whichever
      // direction the shopper is coming from.
      collarTab: profile.category === 'sighthound' ? 'wide' : 'classic',
    });
  };

  // Closing the list by picking a suggestion (applyBreedSelection above)
  // stays instant — the shopper's attention has already moved to the
  // result. Closing WITHOUT a pick (blur, tapping elsewhere) instead
  // fades the list out over a short window with the space still
  // reserved (see .lb-sg-autocomplete-list--closing in the CSS) before
  // actually removing it, rather than collapsing ~260px in one frame. A
  // fast second tap that would otherwise land on whatever control
  // reflows into that space lands on the still-present (fading,
  // non-interactive) list instead.
  proto.closeBreedListWithGrace = function () {
    var self = this;
    if (!this.state.showBreedList) return;
    this.setState({ breedListClosing: true });
    clearTimeout(this._breedListCloseTimer);
    this._breedListCloseTimer = setTimeout(function () {
      self.setState({ showBreedList: false, breedListClosing: false });
    }, 180);
  };

  /* ---------- Core derivation (sizing via FL, pricing/imagery via real Shopify data) ---------- */
  proto.deriveVals = function () {
    var self = this;
    var s = this.state;
    var unitLabel = s.unit === 'cm' ? 'cm' : 'in';
    var breed = this.currentBreedProfile();
    var neckSet = s.neck !== null, chestSet = s.chest !== null;
    // s.neck/s.chest are now stored at full float precision (see inToCm),
    // so a value that started as a typed inch measurement can carry long
    // decimals (14.4in -> 36.576cm) — round display-only to 2dp in either
    // unit so the field never shows raw float noise. This never touches
    // the stored cm value itself, only what's rendered.
    var neckDisplay = !neckSet ? '' : (s.unit === 'cm' ? round2(s.neck) : cmToInPrecise(s.neck));
    var chestDisplay = !chestSet ? '' : (s.unit === 'cm' ? round2(s.chest) : cmToInPrecise(s.chest));

    var neckEval = neckSet ? FL.evaluateMeasurement('neck', s.neck, { breedProfile: breed, ageProfile: s.ageProfile }) : null;
    var chestEval = chestSet ? FL.evaluateMeasurement('chest', s.chest, { breedProfile: breed, ageProfile: s.ageProfile }) : null;

    var neckBlocked = neckEval && neckEval.showBoundary && !s.neckBoundaryChoice && !neckEval.isCustom;
    var chestBlocked = chestEval && chestEval.showBoundary && !s.chestBoundaryChoice && !chestEval.isCustom;

    var finalCollarSize = neckSet ? FL.computeSizeLetter('neck', s.neck, s.neckBoundaryChoice) : null;
    var finalHarnessSize = chestSet ? FL.computeSizeLetter('chest', s.chest, s.chestBoundaryChoice) : null;

    var collarInScope = s.view === 'collar' || s.view === 'all';
    var harnessInScope = s.view === 'harness' || s.view === 'all';
    var neckActive = collarInScope && neckSet;
    var chestActive = harnessInScope && chestSet;

    var neckResolved = neckActive && !neckBlocked && !(neckEval && neckEval.isCustom);
    var chestResolved = chestActive && !chestBlocked && !(chestEval && chestEval.isCustom);
    var isMeasurementUnset = !neckActive && !chestActive;
    var scenarioBoth = neckResolved && chestResolved;
    var scenarioCollar = neckResolved && !chestResolved;
    var scenarioHarness = chestResolved && !neckResolved;
    var scenario = scenarioBoth ? 'both' : (scenarioCollar ? 'collar' : 'harness');
    var isPuppy = s.ageProfile === 'puppy';

    // Trailing 'cm' stripped because sizeRangeLabel bakes the unit into
    // its string while every consumer of these labels (verdict box,
    // included-meta rows) appends ' ' + unitLabel itself — without the
    // strip, cm mode rendered "32–37cm cm". The inches branch below
    // rebuilds the label unitless already.
    var neckRangeLabel = finalCollarSize ? FL.sizeRangeLabel(finalCollarSize, FL.NECK_NODES, FL.COLLAR_SIZES, 'necks').replace('Fits necks ', '').replace(/cm$/, '') : '—';
    var chestRangeLabel = finalHarnessSize ? FL.sizeRangeLabel(finalHarnessSize, FL.CHEST_NODES, FL.HARNESS_SIZES, 'chests').replace('Fits chests ', '').replace(/cm$/, '') : '—';
    if (s.unit === 'in' && finalCollarSize) {
      var ni = FL.COLLAR_SIZES.indexOf(finalCollarSize);
      neckRangeLabel = cmToIn(FL.NECK_NODES[ni]) + ' - ' + cmToIn(FL.NECK_NODES[ni + 1]);
    }
    if (s.unit === 'in' && finalHarnessSize) {
      var hi = FL.HARNESS_SIZES.indexOf(finalHarnessSize);
      chestRangeLabel = cmToIn(FL.CHEST_NODES[hi]) + ' - ' + cmToIn(FL.CHEST_NODES[hi + 1]);
    }

    var colorKey = (FL.LEATHERS.filter(function (c) { return c.name === s.wizardColor; })[0] || {}).key || 'black';

    /* ---- Multi-family collar recommendation — every resolvable main
       family (e.g. BOTH Aeris Classic and Vero Classic for "standard"),
       plus rule.alt as cross-sells, instead of a single silent rule.main[0]
       pick. Each is resolved against REAL variants; only families with a
       real available variant are offered as a choice. ---- */
    var rule = FL.RECOMMENDATION_RULES[breed.category] || FL.RECOMMENDATION_RULES.standard;
    function resolveFamilyOption(familyKey) {
      if (!finalCollarSize) return null;
      var product = productFor(self.productMap, familyKey, colorKey);
      if (!product) return null;
      var family = FL.PRODUCT_FAMILIES[familyKey];
      var allowedWidths = FL.getAllowedWidths(familyKey, breed.category, finalCollarSize);
      // Stock reality, same as the drawer's buildProductCards: only offer a
      // width this exact mapped product actually carries. A product with
      // no real Width option at all (single SKU) collapses to the
      // family's fixed defaultWidth instead, gated by at least one
      // allowed width existing.
      var widths = productCarriesWidthData(product)
        ? allowedWidths.filter(function (w) { return productHasWidth(product, w); })
        : (allowedWidths.length ? [family.defaultWidth || allowedWidths[0]] : []);
      if (!widths.length) return null;
      var preferred = FL.pickPrimaryWidth(familyKey, breed.category, finalCollarSize);
      var width = widths.indexOf(preferred) !== -1 ? preferred : widths[0];
      var variant = resolveVariant(product, { size: finalCollarSize, width: width });
      if (!variant) return null;
      return { familyKey: familyKey, label: familyLabel(familyKey), width: width, variant: variant, cents: variant.price };
    }
    var collarNeeded = scenarioCollar || scenarioBoth;
    var mainCollarOptions = collarNeeded ? rule.main.map(resolveFamilyOption).filter(Boolean) : [];
    var altCollarOptions = collarNeeded ? rule.alt.map(resolveFamilyOption).filter(Boolean) : [];
    // Re-prioritize (never hide) the recommended set to match the shopper's
    // own Classic/Wide tab pick — previously this always led with
    // rule.main[0] regardless of which tab was active, so a shopper who
    // explicitly tapped "Wide / Hound Collar" still saw Classic families
    // listed first below. Only reorders within mainCollarOptions — the
    // main/alt split itself (and therefore the "Also available" tag in
    // collarFamilyPicker) is untouched, and each option's variant/price was
    // already resolved above by resolveFamilyOption, so sorting here can't
    // affect sizing or pricing, only which resolved option is offered
    // first (and therefore defaults into chosenFamilyKey below when the
    // shopper hasn't explicitly picked one yet).
    function widthClassOf(o) { return o.width === '4.5cm' ? 'wide' : 'classic'; }
    mainCollarOptions.sort(function (a, b) {
      var aMatch = widthClassOf(a) === s.collarTab, bMatch = widthClassOf(b) === s.collarTab;
      return (bMatch ? 1 : 0) - (aMatch ? 1 : 0);
    });
    var allCollarOptions = mainCollarOptions.concat(altCollarOptions);
    var chosenFamilyKey = (s.selectedCollarFamily && allCollarOptions.some(function (o) { return o.familyKey === s.selectedCollarFamily; }))
      ? s.selectedCollarFamily
      : (allCollarOptions[0] && allCollarOptions[0].familyKey);
    var chosenCollarOption = allCollarOptions.filter(function (o) { return o.familyKey === chosenFamilyKey; })[0] || null;
    var collarWidth = chosenCollarOption ? chosenCollarOption.width : null;
    var collarVariant = chosenCollarOption ? chosenCollarOption.variant : null;
    var collarCents = collarVariant ? collarVariant.price : null;

    var leashWidth = (scenarioBoth || scenarioHarness) && finalHarnessSize
      ? FL.LEASH_WIDTH_BY_HARNESS_SIZE[finalHarnessSize]
      : (finalCollarSize ? FL.LEASH_WIDTH_BY_COLLAR_SIZE[finalCollarSize] : null);

    var isKit = s.bundle === 'walkkit';
    var productLine = chosenFamilyKey ? familyLabel(chosenFamilyKey) : 'Vero Classic';
    var productTitle = scenarioBoth
      ? (isKit ? productLine + ' Collar, Leash & Harness in ' + s.wizardColor : productLine + ' Collar & Harness in ' + s.wizardColor)
      : scenarioCollar
        ? (isKit ? productLine + ' Collar & Leash in ' + s.wizardColor : productLine + ' Collar in ' + s.wizardColor)
        : (isKit ? 'Vero Harness & Leash in ' + s.wizardColor : 'Vero Harness in ' + s.wizardColor);

    var whyText = (FL.WHY_TEXT[breed.category] || FL.WHY_TEXT.standard)(breed);
    if (isPuppy) whyText += ' Sized to their exact current measurements — no upsizing — since a loose fit isn\'t safe for a growing puppy.';
    var puppyDisclaimer = null;
    if (isPuppy) {
      if (scenarioBoth) puppyDisclaimer = (FL.PUPPY_COLLAR_DISCLAIMER[breed.category] || '') + ' ' + FL.PUPPY_HARNESS_DISCLAIMER;
      else if (scenarioCollar) puppyDisclaimer = FL.PUPPY_COLLAR_DISCLAIMER[breed.category] || '';
      else if (scenarioHarness) puppyDisclaimer = FL.PUPPY_HARNESS_DISCLAIMER;
    }

    /* ---- Real Shopify pricing ---- */
    var harnessProduct = productFor(this.productMap, 'harness', colorKey);
    var harnessVariant = (scenarioHarness || scenarioBoth) && finalHarnessSize
      ? resolveVariant(harnessProduct, { size: finalHarnessSize }) : null;
    var leashFamily = s.leashStyle === 'Hands Free' ? 'leash-hands-free' : 'leash';
    var leashProduct = productFor(this.productMap, leashFamily, colorKey);
    var leashVariant = leashWidth ? resolveVariant(leashProduct, { width: leashWidth }) : null;

    var harnessCents = harnessVariant ? harnessVariant.price : null;
    var leashCents = leashVariant ? leashVariant.price : null;

    function priceLine(label, cents) { return label + ' ' + money(cents, self.moneyFormat); }

    var requiredCents = scenarioBoth ? [collarCents, harnessCents] : scenarioCollar ? [collarCents] : [harnessCents];
    var isPriceAvailable = requiredCents.every(function (c) { return c != null; });

    var standardCents = null, standardLabel = '', standardBreakdown = null;
    var kitLabel = '', kitDiscountName = '', kitBreakdown = null, kitPricing = null;
    if (isPriceAvailable) {
      if (scenarioBoth) {
        standardCents = collarCents + harnessCents;
        standardLabel = 'Just the Collar & Harness';
        standardBreakdown = priceLine('Collar', collarCents) + ' + ' + priceLine('Harness', harnessCents);
        kitLabel = 'Complete Bundle — Fully Equipped';
        kitDiscountName = 'Bundle';
        if (leashCents != null) {
          kitBreakdown = priceLine('Collar', collarCents) + ' + ' + priceLine('Harness', harnessCents) + ' + ' + priceLine('Leash', leashCents);
          kitPricing = FL.computeBundlePricing([collarCents, harnessCents, leashCents], this.bundleDiscountRate);
        }
      } else if (scenarioCollar) {
        standardCents = collarCents;
        standardLabel = 'Just the Collar';
        kitLabel = "Walk Kit — Your Dog's Complete Fit";
        kitDiscountName = 'Walk Kit';
        if (leashCents != null) {
          kitBreakdown = priceLine('Collar', collarCents) + ' + ' + priceLine('Leash', leashCents);
          kitPricing = FL.computeBundlePricing([collarCents, leashCents], this.bundleDiscountRate);
        }
      } else {
        standardCents = harnessCents;
        standardLabel = 'Just the Harness';
        kitLabel = "Walk Kit — Your Dog's Complete Fit";
        kitDiscountName = 'Walk Kit';
        if (leashCents != null) {
          kitBreakdown = priceLine('Harness', harnessCents) + ' + ' + priceLine('Leash', leashCents);
          kitPricing = FL.computeBundlePricing([harnessCents, leashCents], this.bundleDiscountRate);
        }
      }
      var discountPercentLabel = Math.round(this.bundleDiscountRate * 100);
      if (kitPricing) {
        kitBreakdown = kitBreakdown + ' = ' + money(kitPricing.setCents, this.moneyFormat) + ' → ' + money(kitPricing.discountedCents, this.moneyFormat) + ' with ' + discountPercentLabel + '% ' + kitDiscountName + ' discount';
      }
    }

    var isKitSelected = s.bundle === 'walkkit' && !!kitPricing;
    var chosenCents = isKitSelected ? kitPricing.discountedCents : standardCents;
    var ctaLabel = !isPriceAvailable ? 'Currently Unavailable in ' + s.wizardColor
      : (isKitSelected ? (scenario === 'both' ? 'Add Complete Bundle' : 'Add Walk Kit to Cart') : (scenario === 'both' ? 'Add Bundle to Cart' : 'Add ' + (scenario === 'collar' ? 'Collar' : 'Harness') + ' to Cart')) + ' — ' + money(chosenCents, this.moneyFormat);

    var sizesSummaryText = [neckResolved ? ('Collar ' + finalCollarSize) : null, chestResolved ? ('Harness ' + finalHarnessSize) : null].filter(Boolean).join(' · ');

    /* ---- Real kit imagery (Walk Kit / Complete Bundle) ---- */
    var kitAsset = null;
    if (isKitSelected) {
      var kitTierKey = scenarioBoth ? 'bundle' : 'walkkit';
      var kitDesignId = (scenarioHarness && !scenarioBoth) ? 'Harness' : (chosenFamilyKey ? designIdentifierForFamily(chosenFamilyKey) : null);
      var kitWidthKey = (scenarioHarness && !scenarioBoth) ? '_' : collarWidth;
      if (kitDesignId) kitAsset = resolveKitAsset(this.productMap, kitTierKey, kitDesignId, colorKey, s.leashStyle, kitWidthKey);
    }
    // Falls back to the resolved single-item's own real product photo when
    // no kit-imagery match exists yet — never a broken/fabricated image.
    var fallbackImage = (collarVariant && collarVariant.image) || (harnessVariant && harnessVariant.image) || null;

    var cartItems = [];
    if ((scenarioCollar || scenarioBoth) && collarVariant) cartItems.push(collarVariant.id);
    if ((scenarioHarness || scenarioBoth) && harnessVariant) cartItems.push(harnessVariant.id);
    if (isKitSelected && leashVariant) cartItems.push(leashVariant.id);

    return {
      unitLabel, breed, neckSet, chestSet, neckDisplay, chestDisplay,
      neckEval, chestEval, neckBlocked, chestBlocked,
      finalCollarSize, finalHarnessSize, neckRangeLabel, chestRangeLabel,
      neckResolved, chestResolved, isMeasurementUnset, scenarioBoth, scenarioCollar, scenarioHarness,
      isCustomPending: !!((neckEval && neckEval.isCustom) || (chestEval && chestEval.isCustom)) && !(neckResolved || chestResolved),
      isSizePending: !isMeasurementUnset && !(neckResolved || chestResolved) && !((neckEval && neckEval.isCustom) || (chestEval && chestEval.isCustom)),
      isSizeResolved: neckResolved || chestResolved,
      collarWidth, leashWidth, productTitle, whyText, puppyDisclaimer, isPuppy,
      mainCollarOptions, altCollarOptions, allCollarOptions, chosenFamilyKey,
      isPriceAvailable, standardCents, standardLabel, standardBreakdown, kitLabel, kitBreakdown, kitPricing,
      ctaLabel, sizesSummaryText, cartItems, kitAsset, fallbackImage, colorKey, chosenCents,
    };
  };

  /* ---------- Markup builders ---------- */

  // No artificial cap — .lb-sg-autocomplete-list already scrolls (see its
  // max-height/overflow-y in the CSS), so every match stays reachable
  // instead of silently discarding everything past the first few. An
  // empty query used to hard-slice to the first 8 breeds alphabetically
  // (always "A..." names), which read as "that's the whole list" rather
  // than a searchable 117-breed directory. Matching also now ranks
  // "starts with" before "contains anywhere", same relevance order as
  // the drawer's own combobox (e.g. typing "hound" surfaces Greyhound
  // before Afghan Hound, not the reverse alphabetical-only order).
  proto.breedAutocompleteList = function () {
    var q = this.state.breedQuery.trim().toLowerCase();
    var matches;
    if (!q) {
      matches = FL.BREED_NAMES;
    } else {
      var starts = [], contains = [];
      FL.BREED_NAMES.forEach(function (n) {
        var l = n.toLowerCase();
        if (l.indexOf(q) === 0) starts.push(n);
        else if (l.indexOf(q) !== -1) contains.push(n);
      });
      matches = starts.concat(contains);
    }
    if (matches.length === 0) return '<div class="lb-sg-autocomplete-empty">No match — try “Mixed Breed / Other” or continue typing.</div>';
    var hint = q
      ? '<div class="lb-sg-autocomplete-hint">' + matches.length + ' match' + (matches.length === 1 ? '' : 'es') + '</div>'
      : '<div class="lb-sg-autocomplete-hint">' + FL.BREED_NAMES.length + ' breeds — scroll or keep typing to narrow</div>';
    return hint + matches.map(function (n) {
      return '<button type="button" class="lb-sg-autocomplete-item" data-action="pickBreed" data-name="' + esc(n) + '">' + esc(n) + '</button>';
    }).join('') + '<button type="button" class="lb-sg-autocomplete-item" data-action="pickBreed" data-name="' + esc(FL.FALLBACK_BREED_NAME) + '">' + esc(FL.FALLBACK_BREED_NAME) + '</button>';
  };

  proto.unitToggle = function () {
    var s = this.state;
    return '<div class="lb-sg-unit">' +
      '<button data-action="setUnit" data-unit="cm" data-on="' + (s.unit === 'cm') + '">CM</button>' +
      '<button data-action="setUnit" data-unit="in" data-on="' + (s.unit === 'in') + '">IN</button>' +
      '</div>';
  };

  // A second CM/IN toggle, right next to each stepper, so a shopper who
  // scrolled straight to "Neck size:" isn't forced to scroll back up to
  // the shared panel just to switch units. Dispatches the exact same
  // data-action="setUnit" as the main toggle above — both read/write the
  // single s.unit field, so this one is never a separate source of truth
  // to keep in sync; picking either toggle updates both instantly on the
  // next render.
  proto.unitToggleCompact = function () {
    var s = this.state;
    return '<div class="lb-sg-unit-compact">' +
      '<button data-action="setUnit" data-unit="cm" data-on="' + (s.unit === 'cm') + '">cm</button>' +
      '<button data-action="setUnit" data-unit="in" data-on="' + (s.unit === 'in') + '">in</button>' +
      '</div>';
  };

  proto.buildOptionsMarkup = function () {
    var s = this.state;
    var placeholder = '<option value=""' + (!s.bodyBuild ? ' selected' : '') + ' disabled>Select a breed, or choose a build manually…</option>';
    return placeholder + FL.BUILD_OPTIONS.map(function (b) {
      return '<option value="' + esc(b) + '"' + (b === s.bodyBuild ? ' selected' : '') + '>' + esc(FL.BUILD_OPTION_LABELS[b] || b) + '</option>';
    }).join('');
  };

  proto.tooltip = function (text) {
    return '<span class="lb-sg-tooltip">' +
      '<button type="button" class="lb-sg-info-btn" aria-label="Why this size"><span aria-hidden="true">ⓘ</span></button>' +
      '<span class="lb-sg-tooltip-bubble" role="tooltip">' + esc(text) + '</span>' +
      '</span>';
  };

  // Shared measurement-input panel (breed autocomplete + unit toggle +
  // age/build) — rendered ONCE on "View All" (previously duplicated
  // verbatim inside both the Collar and Harness sections, which read as
  // two disconnected controls even though they shared one underlying
  // state). Rendered per-section only on the single-item tabs, where
  // there's exactly one copy on screen either way.
  proto.sharedControlsPanel = function () {
    return '<div class="lb-sg-panel lb-sg-panel--shared">' +
      '<div class="lb-sg-panel-row">' + this.unitToggle() + this.breedAutocomplete() + '</div>' +
      this.ageBuildControls() +
      '</div>';
  };

  // Width copy, keyed by the REAL width strings the product data uses
  // (FL.PRODUCT_FAMILIES widths / deriveVals' collarWidth) — not a
  // parallel mm scale. verdict = the reason line in the verdict box,
  // family = the benefit line on a family-picker card. One map so the
  // same width never gets two conflicting stories.
  var WIDTH_VERDICTS = {
    '2cm': { cm: '2 cm', verdict: 'featherlight on a delicate neck.', family: 'featherlight on delicate necks.' },
    '3cm': { cm: '3 cm', verdict: 'balanced strength for everyday walks.', family: 'balanced for daily wear.' },
    '4.5cm': { cm: '4.5 cm', verdict: 'spreads leash pressure for strong pullers.', family: 'spreads pressure for strong pullers.' },
  };

  // The living spec line (collar measure block only — harness straps use
  // a different width scale entirely, see the sizingTable width-ratio
  // footnote). One permanent line, no interaction; only its copy changes:
  //   - unresolved: ambient statement that widths exist and are matched
  //   - resolved + width certain: the width, stated as a decision
  //   - resolved + width still branching: defers to the family picker,
  //     where that choice actually happens — never a guess
  // "Certain" is derived from the real resolved family options (every
  // offerable family carries the same width), not from which tab is
  // active — so a Classic-tab shopper whose size makes every family 3cm
  // gets the real answer too, and the Wide tab stays certain by fiat
  // (both Wide families only ship 4.5cm) even before products resolve.
  proto.collarSpecLine = function (d) {
    var s = this.state;
    var html, resolved = false;
    if (d.neckResolved) {
      resolved = true;
      var wv = null;
      if (s.selectedCollarFamily && s.selectedCollarFamily === d.chosenFamilyKey) {
        // The shopper explicitly picked a family below — their choice IS
        // the cut now, even if it's the cross-sell; saying anything else
        // here would contradict the verdict box.
        wv = WIDTH_VERDICTS[d.collarWidth] || null;
      } else {
        // No explicit choice yet: certainty from the RECOMMENDED (main)
        // families only — the wide cross-sell in allCollarOptions is an
        // opt-in alternative, and counting it here would make almost
        // every classic match "uncertain" for the sake of an option
        // most shoppers never pick.
        var pool = d.mainCollarOptions.length ? d.mainCollarOptions : d.allCollarOptions;
        var widths = [];
        pool.forEach(function (o) { if (widths.indexOf(o.width) === -1) widths.push(o.width); });
        if (!widths.length && s.collarTab === 'wide') widths = ['4.5cm'];
        wv = widths.length === 1 ? WIDTH_VERDICTS[widths[0]] || null : null;
      }
      html = wv
        ? 'Your cut: <span class="n">' + wv.cm + '</span> — ' + wv.verdict
        // "...confirmed with your collar style below" only holds when a
        // picker actually renders below (collarFamilyPicker returns '' for
        // fewer than 2 options — see its own length check) — with every
        // family out of stock in this color (allCollarOptions empty), that
        // would point at a section that doesn't exist. wv is only null
        // here when the pool resolved to either 0 or 2+ widths (a single
        // resolved width already took the branch above), so this length
        // check cleanly distinguishes the two.
        : (d.allCollarOptions.length >= 2
            ? 'Sized <span class="n">' + d.finalCollarSize + '</span> — final width confirmed with your collar style below.'
            : 'Sized <span class="n">' + d.finalCollarSize + '</span> — width confirmed once this size is back in stock in ' + esc(s.wizardColor) + '.');
    } else {
      html = 'Cut in three widths — 2, 3 &amp; 4.5 cm, matched to neck and strength.';
    }
    return '<div class="lb-sg-spec-line' + (resolved ? ' resolved' : '') + '" aria-live="polite">' + html + '</div>';
  };

  proto.measureBlock = function (key, d) {
    var self = this;
    var s = this.state;
    var isNeck = key === 'neck';
    var evalResult = isNeck ? d.neckEval : d.chestEval;
    // While this field is focused AND the shopper has typed since focusing
    // (_rawMeasureText holds their literal, un-rounded keystrokes), show
    // exactly that — never the derived/rounded state value. Otherwise
    // (fresh render, unfocused, or just-blurred) show the clean formatted
    // value. This is what stops a mid-type re-render from overwriting a
    // partial value like "10." or "10,5" — see the 'input'/'focusout'
    // wiring in _wireEvents.
    var rawTyped = self._rawMeasureText[key];
    var display = (self._activeFocusId === key && rawTyped !== null) ? rawTyped : (isNeck ? d.neckDisplay : d.chestDisplay);
    var boundaryChoice = isNeck ? s.neckBoundaryChoice : s.chestBoundaryChoice;
    var fallbackInc = isNeck ? (s.unit === 'cm' ? 34 : cmToIn(34)) : (s.unit === 'cm' ? 52 : cmToIn(52));
    var fallbackDec = isNeck ? (s.unit === 'cm' ? 36 : cmToIn(36)) : (s.unit === 'cm' ? 54 : cmToIn(54));

    var html = '<div class="lb-sg-field-row">' +
      '<span class="lb-sg-field-label">' + (isNeck ? 'Neck size:' : 'Chest size:') + '</span>' +
      '<div class="lb-sg-stepper">' +
      '<button data-action="step' + (isNeck ? 'Neck' : 'Chest') + '" data-dir="dec" data-fallback="' + fallbackDec + '" aria-label="Decrease">–</button>' +
      '<input type="text" inputmode="decimal" data-input="' + key + '" placeholder="e.g. ' + (isNeck ? 28 : 40) + '" value="' + esc(String(display)) + '">' +
      '<button data-action="step' + (isNeck ? 'Neck' : 'Chest') + '" data-dir="inc" data-fallback="' + fallbackInc + '" aria-label="Increase">+</button>' +
      '</div>' +
      // The compact toggle is only useful in "View All," where both the
      // collar and harness measure blocks are on screen at once and a
      // shopper working through both could plausibly want to flip units
      // mid-flow. In the single-item Collar-only/Harness-only views the
      // shared panel's own CM/IN toggle sits right above this one stepper
      // — a second toggle a few pixels away is pure duplication, not a
      // convenience. Falls back to the plain unit-label text those views
      // already had.
      (s.view === 'all' ? self.unitToggleCompact() : '<span>' + d.unitLabel + '</span>') +
      '</div>';

    if (evalResult) {
      html += '<div class="lb-sg-sizelabel" aria-live="polite">' + evalResult.sizeLabel + '</div>';
      // Amber ("not unusual, just worth a second look") is intentionally
      // quieter than red ("double-check this") — a boxed/bordered alert
      // for both tones gave the common, low-stakes amber case the same
      // visual alarm as a genuine one.
      if (evalResult.showGuardrail) {
        if (evalResult.guardrailTone === 'red') {
          html += '<div class="lb-sg-guardrail lb-sg-reveal" data-tone="red">' + evalResult.guardrailMessage + '</div>';
        } else {
          html += '<p class="lb-sg-guardrail-quiet lb-sg-reveal">' + evalResult.guardrailMessage + '</p>';
        }
      }
      if (evalResult.isCustom) {
        html += '<div class="lb-sg-custom-banner lb-sg-reveal">' +
          '<p>' + FL.customBannerText(isNeck, !isNeck) + '</p>' +
          '<a class="lb-sg-cta" href="' + FL.buildCustomSizeMailto({
            breedName: s.breedName, ageProfile: s.ageProfile, breedProfile: self.currentBreedProfile(),
            neckSize: s.neck, chestSize: s.chest, isCustomCollar: isNeck, isCustomHarness: !isNeck,
            supportEmail: self.supportEmail,
          }) + '">Request Custom Size</a>' +
          '</div>';
      } else if (evalResult.showBoundary) {
        // Matches lillebjorn-fit-finder.js's boundaryMarkup exactly (same
        // header text via boundaryHeaderText, same button copy) — a
        // shopper on a genuine size-letter tie sees an identical prompt in
        // either tool, and this measurement stays blocked (see
        // neckBlocked/chestBlocked in deriveVals) until they answer.
        html += '<div class="lb-sg-boundary lb-sg-reveal">' +
          '<div class="lb-sg-boundary-copy">' + evalResult.boundaryHeaderText + '</div>' +
          '<div class="lb-sg-boundary-btns">' +
          '<button data-action="setBoundary" data-key="' + key + '" data-pref="loose" data-on="' + (boundaryChoice === 'loose') + '">LOOSE / RELAXED MEASUREMENT</button>' +
          '<button data-action="setBoundary" data-key="' + key + '" data-pref="snug" data-on="' + (boundaryChoice === 'snug') + '">SNUG / EXACT SKIN MEASUREMENT</button>' +
          '</div></div>';
      }
    }

    // Deliberately outside the `if (evalResult)` gate above — the spec
    // line exists before any measurement (ambient copy), so width is
    // explained pre-emptively rather than behind a click. Collar only:
    // harness straps use a different width scale entirely.
    if (isNeck) html += self.collarSpecLine(d);

    return html;
  };

  proto.sizingTable = function (kind, d) {
    var self = this;
    var s = this.state;
    var isCollar = kind === 'collar';
    var isWide = isCollar && s.collarTab === 'wide';
    var sizes = isCollar ? FL.COLLAR_SIZES : FL.HARNESS_SIZES;
    var nodes = isCollar ? FL.NECK_NODES : FL.CHEST_NODES;
    var breedsMap = isCollar ? BREEDS_BY_COLLAR_SIZE : BREEDS_BY_HARNESS_SIZE;
    var matchLetter = isCollar ? d.finalCollarSize : d.finalHarnessSize;
    var matchActive = isCollar ? d.neckResolved : d.chestResolved;
    var columnLabel = (isCollar ? 'Neck' : 'Chest Girth') + ' (' + d.unitLabel + ')';

    // Computed once, shared by both the desktop <table> below and the
    // <=680px mobile accordion right after it — same size/range/match
    // data, just laid out differently per breakpoint (see the
    // .lb-sg-table-wrap / .lb-sg-mtable-wrap display:none pair in the
    // CSS, which is what actually decides which one a shopper sees).
    var rowsData = sizes.map(function (size, i) {
      var lo = nodes[i], hi = nodes[i + 1];
      var rangeLabel = s.unit === 'cm' ? (lo + ' - ' + hi) : (cmToIn(lo) + ' - ' + cmToIn(hi));
      return { size: size, rangeLabel: rangeLabel, isMatch: matchActive && size === matchLetter };
    });

    var rows = rowsData.map(function (row) {
      var breedsInfo = topBreedsForSize(breedsMap[row.size]);
      var popoverId = kind + '-' + row.size;
      var breedsCell = esc(breedsInfo.shown) + (breedsInfo.extra.length ? ' <span class="lb-sg-breeds-more-wrap">' +
        '<button type="button" class="lb-sg-breeds-more" data-action="toggleBreedsPopover" data-popover="' + popoverId + '">+ more</button>' +
        (s.openBreedsPopover === popoverId ? '<div class="lb-sg-breeds-popover">' + esc(breedsInfo.extra.join(', ')) + '</div>' : '') +
        '</span>' : '');
      var widthCell = isWide ? '<td class="lb-sg-td-mid">' + (row.size === 'L' || row.size === 'XL' ? '3 : 4.5' : '1.5 : 4.5') + '</td>' : '';
      return '<tr data-match="' + row.isMatch + '">' +
        '<td class="lb-sg-td-size">' + row.size + '</td>' +
        '<td class="lb-sg-td-mid">' + row.rangeLabel + '</td>' +
        widthCell +
        '<td class="lb-sg-td-breeds">' + breedsCell + '</td>' +
        '<td class="lb-sg-td-match">' + (row.isMatch ? '<span class="lb-sg-pill">Your Fit</span>' : '') + '</td>' +
        '</tr>';
    }).join('');

    var tableRevealClass = (isCollar && this._justSwitchedCollarTab) ? ' lb-sg-reveal' : '';
    // The width-ratio note is a genuine, actionable product fact (it
    // explains a real construction difference the shopper is about to pay
    // for), not a footnote — styled as its own info callout so it reads as
    // "worth knowing" rather than fine print, and stays visually distinct
    // from the disclaimer below (which IS fine print).
    var widthNoteText = isWide
      ? 'For larger breeds, we have designed a slightly different collar width ratio for L and XL sizes to ensure more security and comfort.'
      : (!isCollar ? 'For sizes L and XL, straps are slightly wider (2–3 cm) than other sizes (1.5–2 cm).' : '');
    var footnotesHtml = widthNoteText
      ? '<div class="lb-sg-info-callout"><span class="lb-sg-info-callout-icon">i</span><p>' + widthNoteText + '</p></div>'
      : '';
    // The divider (top rule on desktop, tighter top margin on mobile) only
    // makes sense when the width-ratio callout actually precedes this note
    // — otherwise it's a floating rule with nothing to divide (see
    // .lb-sg-fit-disclaimer--divided in the CSS).
    var disclaimerHtml = '<p class="lb-sg-fit-disclaimer' + (widthNoteText ? ' lb-sg-fit-disclaimer--divided' : '') + '">Note: Breed recommendations are based on average sizing. Because every dog is unique, we strongly recommend measuring your dog prior to ordering.</p>';

    var desktopHtml = '<div class="lb-sg-table-wrap' + tableRevealClass + '"><table class="lb-sg-table"><thead><tr>' +
      '<th style="width:64px;">Size</th>' +
      '<th style="width:120px;">' + columnLabel + '</th>' +
      (isWide ? '<th style="width:120px;">Width Ratio (cm)</th>' : '') +
      '<th>Recommended Breeds</th><th style="width:92px;"></th>' +
      '</tr></thead><tbody>' + rows + '</tbody></table>' +
      footnotesHtml + disclaimerHtml +
      '</div>';

    // ---- Mobile accordion (<=680px) ----
    // No "+ more" popover here — the row is already gated behind a tap,
    // so a nested second disclosure would be one tap too many. Row
    // open/closed state persists in this.state.mobileRowOpen across
    // re-renders (see the toggleMobileRow action) — absent from that map
    // means "open only if this is the matched row," present means the
    // shopper explicitly overrode that. Rows never close each other, so
    // a shopper on a size boundary can compare two adjacent breed lists
    // at once.
    var mrows = rowsData.map(function (row) {
      var rowKey = kind + '-' + row.size;
      var isOpen = self.state.mobileRowOpen.hasOwnProperty(rowKey) ? self.state.mobileRowOpen[rowKey] : row.isMatch;
      var widthLine = isWide ? '<div class="lb-sg-mrow-width">Width ratio ' + (row.size === 'L' || row.size === 'XL' ? '3 : 4.5' : '1.5 : 4.5') + ' cm</div>' : '';
      // Own popover id (m- prefix), distinct from the desktop table's for
      // the same size — both markups exist in the DOM at once (CSS alone
      // decides which is visible per breakpoint), so sharing one id could
      // leave a popover open on the hidden variant when the viewport
      // crosses the 680px breakpoint.
      var mBreedsInfo = topBreedsForSize(breedsMap[row.size], BREEDS_DISPLAY_LIMIT_MOBILE);
      var mPopoverId = 'm-' + kind + '-' + row.size;
      var mBreedsCell = esc(mBreedsInfo.shown) + (mBreedsInfo.extra.length ? ' <span class="lb-sg-breeds-more-wrap">' +
        '<button type="button" class="lb-sg-breeds-more" data-action="toggleBreedsPopover" data-popover="' + mPopoverId + '">+ more</button>' +
        (s.openBreedsPopover === mPopoverId ? '<div class="lb-sg-breeds-popover">' + esc(mBreedsInfo.extra.join(', ')) + '</div>' : '') +
        '</span>' : '');
      return '<div class="lb-sg-mrow" data-match="' + row.isMatch + '" data-open="' + isOpen + '">' +
        '<button type="button" class="lb-sg-mrow-head" data-action="toggleMobileRow" data-key="' + esc(rowKey) + '">' +
        '<span class="col-size"><span class="size-letter">' + row.size + '</span></span>' +
        '<span class="size-range">' + row.rangeLabel + '</span>' +
        (row.isMatch ? '<span class="fit-badge">Your Fit</span>' : '') +
        '<span class="mrow-spacer"></span>' +
        '<span class="chevron"></span>' +
        '</button>' +
        '<div class="lb-sg-mrow-body"><div class="lb-sg-mrow-body-inner">' +
        widthLine +
        '<span class="breeds-text">' + mBreedsCell + '</span>' +
        '</div></div>' +
        '</div>';
    }).join('');

    var mobileHtml = '<div class="lb-sg-mtable-wrap">' +
      '<div class="lb-sg-mheader"><span class="col-size">Size</span><span style="margin-left:24px;">' + esc(columnLabel) + '</span></div>' +
      '<div>' + mrows + '</div>' +
      footnotesHtml + disclaimerHtml +
      '</div>';

    return desktopHtml + mobileHtml;
  };

  proto.breedAutocomplete = function () {
    var s = this.state;
    return '<div class="lb-sg-autocomplete">' +
      '<input type="text" data-input="breed" placeholder="Search breed…" value="' + esc(s.breedQuery) + '" autocomplete="off">' +
      ((s.showBreedList || s.breedListClosing) ? '<div class="lb-sg-autocomplete-list' + (s.breedListClosing ? ' lb-sg-autocomplete-list--closing' : '') + '">' + this.breedAutocompleteList() + '</div>' : '') +
      '</div>';
  };

  proto.ageBuildControls = function () {
    var s = this.state;
    return '<div class="lb-sg-field-row">' +
      '<div class="lb-sg-segmented">' +
      '<button data-action="setAge" data-age="adult" data-on="' + (s.ageProfile === 'adult') + '">Adult Dog</button>' +
      '<button data-action="setAge" data-age="puppy" data-on="' + (s.ageProfile === 'puppy') + '">Growing Puppy</button>' +
      '</div>' +
      '</div>' +
      (s.ageProfile === 'puppy' ? '<p class="lb-sg-copy" style="margin-top:-8px;">We size to your puppy’s exact current measurements — never larger in advance, since a loose collar isn’t safe.</p>' : '') +
      '<div class="lb-sg-field-row">' +
      '<select class="lb-sg-select" data-input="bodyBuild">' + this.buildOptionsMarkup() + '</select>' +
      '</div>';
  };

  proto.collarSection = function (d, showControls) {
    var s = this.state;
    var showCollar = s.view === 'collar' || s.view === 'all';
    if (!showCollar) return '';
    return '<div class="lb-sg-segtabs">' +
      '<button class="lb-sg-segtab" data-action="selectCollarTab" data-tab="classic" data-on="' + (s.collarTab === 'classic') + '">Classic Collar</button>' +
      '<button class="lb-sg-segtab" data-action="selectCollarTab" data-tab="wide" data-on="' + (s.collarTab === 'wide') + '">Wide / Hound Collar</button>' +
      '</div>' +
      '<h2 class="lb-sg-h2">Collar</h2>' +
      '<div class="lb-sg-grid12">' +
      '<div class="lb-sg-col5">' +
      '<div class="lb-sg-figure"><img src="' + esc(this.neckIllustration) + '" alt="Neck measurement diagram" loading="lazy"></div>' +
      '<p class="lb-sg-copy"><strong>Measure snugly around the middle of your dog\'s neck</strong> with a tape measure, without leaving any extra space. Our collars are true to size, so a snug measurement will ensure the best fit.</p>' +
      '<div class="lb-sg-note-label">Collar Sizing Explained</div>' +
      '<p class="lb-sg-note-body">Each collar size reflects its inner circumference range. For example, a size S (27–32 cm) means the collar\'s inner circumference fits necks measured between 27–32 cm.</p>' +
      (showControls ? this.sharedControlsPanel() : '') +
      '<div class="lb-sg-panel">' +
      this.measureBlock('neck', d) +
      '</div>' +
      (d.neckResolved ? '<button type="button" class="lb-sg-shoplink" data-action="scrollToFit">View Your Fit Below <span aria-hidden="true">↓</span></button>' : '') +
      '</div>' +
      '<div class="lb-sg-col7">' + this.sizingTable('collar', d) + '</div>' +
      '</div>';
  };

  proto.harnessSection = function (d, showControls) {
    var s = this.state;
    var showHarness = s.view === 'harness' || s.view === 'all';
    if (!showHarness) return '';
    var showCollar = s.view === 'all';
    return '<div style="' + (showCollar ? 'border-top:1px solid var(--lbsg-line); padding-top:32px;' : '') + '">' +
      '<h2 class="lb-sg-h2">Harness</h2>' +
      '<div class="lb-sg-grid12">' +
      '<div class="lb-sg-col5">' +
      '<div class="lb-sg-figure"><img src="' + esc(this.chestIllustration) + '" alt="Chest measurement diagram" loading="lazy"></div>' +
      '<p class="lb-sg-copy"><strong>Measure around your dog\'s chest</strong> using a tape measure. For small dogs, allow a little extra space for comfort; for larger breeds, a snug fit is recommended.</p>' +
      (showControls ? this.sharedControlsPanel() : '') +
      '<div class="lb-sg-panel">' +
      this.measureBlock('chest', d) +
      '</div>' +
      (d.chestResolved ? '<button type="button" class="lb-sg-shoplink" data-action="scrollToFit">View Your Fit Below <span aria-hidden="true">↓</span></button>' : '') +
      '</div>' +
      '<div class="lb-sg-col7">' + this.sizingTable('harness', d) + '</div>' +
      '</div></div>';
  };

  proto.resultBox = function (d) {
    // Width joins the verdict — stated in the same breath as the size,
    // from d.collarWidth: the CHOSEN family's real resolved width, not a
    // tab-level guess. The family picker below re-renders this whole
    // area on selection, so this line tracks the shopper's actual
    // configuration live (switching Vero 2cm ↔ Aeris 3cm updates it).
    var wv = d.collarWidth && WIDTH_VERDICTS[d.collarWidth];
    var widthLine = wv
      ? '<div class="lb-sg-verdict-width">Cut in <span class="w">' + wv.cm + '</span> — ' + wv.verdict + '</div>'
      : '';
    if (d.scenarioBoth) return '<div class="lb-sg-verdict">' +
      '<div class="lb-sg-verdict-eyebrow">Your Dog\'s Perfect Set</div>' +
      '<div class="lb-sg-verdict-split">' +
      '<div class="lb-sg-verdict-col"><div class="lb-sg-verdict-size">Collar Size ' + d.finalCollarSize + '</div><div class="lb-sg-verdict-range">Fits necks ' + d.neckRangeLabel + ' ' + d.unitLabel + '</div>' + widthLine + '</div>' +
      '<div class="lb-sg-verdict-divider"></div>' +
      '<div class="lb-sg-verdict-col"><div class="lb-sg-verdict-size">Harness Size ' + d.finalHarnessSize + '</div><div class="lb-sg-verdict-range">Fits chests ' + d.chestRangeLabel + ' ' + d.unitLabel + '</div></div>' +
      '</div></div>';
    var single = (d.scenarioCollar || d.scenarioHarness) && !d.isSizePending;
    if (single) {
      var isCollar = d.scenarioCollar;
      return '<div class="lb-sg-verdict">' +
        '<div class="lb-sg-verdict-eyebrow">Your Matched Size</div>' +
        '<div class="lb-sg-verdict-size">' + (isCollar ? 'Collar' : 'Harness') + ' Size ' + (isCollar ? d.finalCollarSize : d.finalHarnessSize) + '</div>' +
        (isCollar ? widthLine : '') +
        '</div>';
    }
    return '';
  };

  proto.collarFamilyPicker = function (d) {
    if (d.allCollarOptions.length < 2) return '';
    var self = this;
    var mainCount = d.mainCollarOptions.length;
    var cards = d.allCollarOptions.map(function (o, i) {
      var isAlt = i >= mainCount;
      return '<button type="button" class="lb-sg-family-card' + (isAlt ? ' lb-sg-family-card--alt' : '') + '" data-action="selectCollarFamily" data-family="' + esc(o.familyKey) + '" data-on="' + (o.familyKey === d.chosenFamilyKey) + '">' +
        '<span class="lb-sg-family-radio" data-on="' + (o.familyKey === d.chosenFamilyKey) + '"></span>' +
        '<span class="lb-sg-family-copy"><span class="lb-sg-family-name">' + esc(o.label) + (isAlt ? ' <span class="lb-sg-leash-tag">Also available</span>' : '') + '</span>' +
        '<span class="lb-sg-family-desc">' + esc(familyStyleDescriptor(o.familyKey)) + '</span>' +
        // o.width is the width THIS family actually resolves to for the
        // matched size (resolveFamilyOption picked a real variant), so a
        // benefit sentence here is a statement of fact, not marketing.
        '<span class="lb-sg-family-width">' + (WIDTH_VERDICTS[o.width] ? 'Cut in ' + WIDTH_VERDICTS[o.width].cm + ' — ' + WIDTH_VERDICTS[o.width].family : esc(o.width || '')) + '</span></span>' +
        '<span class="lb-sg-family-price">' + money(o.cents, self.moneyFormat) + '</span>' +
        '</button>';
    }).join('');
    return '<div style="margin-bottom:24px;"><div class="lb-sg-included-label">Collar Style</div><div class="lb-sg-family-list">' + cards + '</div></div>';
  };

  proto.fitDetailPanel = function (d) {
    if (d.isMeasurementUnset) return '<div class="lb-sg-empty"><div class="lb-sg-empty-label">Your Custom Fit</div><p class="lb-sg-empty-copy">Enter your dog\'s measurements above to see your matched size and complete the fit.</p></div>';
    if (d.isCustomPending) return '<div class="lb-sg-empty"><div class="lb-sg-empty-label">Your Custom Fit</div><p class="lb-sg-empty-copy">This measurement is past our standard sizing range — request a bespoke order above, and our team will craft it to your dog\'s exact measurements.</p></div>';
    if (d.isSizePending) return '<div class="lb-sg-empty"><div class="lb-sg-empty-label">Your Custom Fit</div><p class="lb-sg-empty-copy">Answer the boundary question above to see your matched size and complete the fit.</p></div>';
    if (!d.isSizeResolved) return '';

    var s = this.state;

    // Color swatches and the collar-family picker are built unconditionally
    // and rendered on every path below, including when this color has no
    // priceable variant (see the !d.isPriceAvailable branch further down).
    // Losing a color's stock shouldn't lock a shopper out of the picker
    // that would let them switch to a color that IS in stock — only the
    // price-dependent tier list and CTA degrade to an inline unavailable
    // state; the swatches themselves stay visible and clickable always.
    var swatches = FL.LEATHERS.map(function (c) {
      var texture = this.swatchTextures[c.key];
      var bg = texture ? 'background-image:url(' + esc(texture) + ');background-size:cover;background-position:center;' : 'background:' + c.hex + ';';
      return '<button type="button" class="lb-sg-swatch" data-action="selectColor" data-color="' + esc(c.name) + '" title="' + esc(c.name) + '" data-on="' + (s.wizardColor === c.name) + '" style="' + bg + '"></button>';
    }, this).join('');
    var colorPickerHtml = '<div style="margin-bottom:24px;">' +
      '<div class="lb-sg-included-label">Leather Color</div>' +
      '<div class="lb-sg-swatches">' + swatches + '</div>' +
      '<div class="lb-sg-color-label">Selected Color: ' + esc(s.wizardColor) + '</div>' +
      '</div>';

    // Real product photo — kit/bundle photography (d.kitAsset, real
    // pre-composed studio shots of the actual Walk Kit/Complete Bundle)
    // takes priority when a kit tier is selected and one resolves, same
    // as the drawer; otherwise falls back to the resolved single-item
    // variant's own real photo (d.fallbackImage). Both are recomputed
    // fresh in deriveVals() on every render, so this updates on its own
    // the instant a shopper changes family, color, or leash style —
    // nothing extra to wire for "dynamic." The frame itself is ALWAYS
    // rendered (reserving its space with the neutral background from
    // .lb-sg-fit-photo) even when neither resolves — e.g. a color with no
    // priceable variant has no real photo to show, but the slot staying
    // empty is a deliberate, honest "no photo yet" state, not a missing
    // element that collapses the whole left column and leaves a gap.
    var fitPhotoUrl = (d.kitAsset && d.kitAsset.url) || d.fallbackImage || '';
    var fitPhotoHtml = '<div class="lb-sg-fit-photo">' +
      (fitPhotoUrl ? '<img src="' + esc(fitPhotoUrl) + '" alt="' + esc(d.productTitle) + '" loading="lazy">' : '') +
      '</div>';

    // "Included in your fit" — built from the resolved SIZE (always known
    // once isSizeResolved is true) rather than gated behind price/variant
    // availability, so an out-of-stock color still shows the collar/harness
    // specs instead of leaving the left column empty. Width sub-details
    // that depend on an actually-resolved variant (d.collarWidth) degrade
    // to an em dash rather than the literal string "null" when unresolved.
    var included = '';
    if (d.scenarioCollar || d.scenarioBoth) included += '<div class="lb-sg-included-row"><span>Collar ' + d.finalCollarSize + '</span><span>' + (d.collarWidth || '—') + '</span></div>';
    if (d.scenarioHarness || d.scenarioBoth) included += '<div class="lb-sg-included-row"><span>Harness ' + d.finalHarnessSize + '</span><span></span></div>';
    included += '<div class="lb-sg-included-row"><span>Leash</span><span>' + (d.leashWidth || '—') + ' · ' + (FL.LEASH_STYLE_DISPLAY_NAME[s.leashStyle] || s.leashStyle) + '</span></div>';
    if (d.scenarioCollar || d.scenarioBoth) included += '<div class="lb-sg-included-meta"><span>Fits necks ' + this.tooltip("Matched to your dog's neck measurement (" + d.finalCollarSize + ") for a secure, comfortable fit.") + '</span><span>' + d.neckRangeLabel + ' ' + d.unitLabel + '</span></div>';
    if (d.scenarioHarness || d.scenarioBoth) included += '<div class="lb-sg-included-meta"><span>Fits chests ' + this.tooltip("Matched to your dog's chest measurement (" + d.finalHarnessSize + ") — distributes pressure evenly across the chest and sternum.") + '</span><span>' + d.chestRangeLabel + ' ' + d.unitLabel + '</span></div>';

    var leftColumnHtml = '<div>' +
      '<div class="lb-sg-fit-eyebrow">Your Custom Fit</div>' +
      fitPhotoHtml +
      '<div class="lb-sg-fit-title">' + esc(d.productTitle) + '</div>' +
      '<p class="lb-sg-fit-why">' + esc(d.whyText) + '</p>' +
      (d.puppyDisclaimer ? '<div class="lb-sg-puppy-note">' + esc(d.puppyDisclaimer) + '</div>' : '') +
      '<div class="lb-sg-included-label">Included in your fit</div>' +
      included +
      '</div>';

    if (!d.isPriceAvailable) {
      return '<div class="lb-sg-fit-grid">' +
        leftColumnHtml +
        '<div>' +
        this.collarFamilyPicker(d) +
        colorPickerHtml +
        '<div class="lb-sg-empty">' +
        '<div class="lb-sg-empty-label">Your Custom Fit</div>' +
        '<p class="lb-sg-empty-copy">This size isn\'t currently available in ' + esc(s.wizardColor) + '. Pick a different color above, or <a href="mailto:' + esc(this.supportEmail) + '">reach out to us</a> for help.</p>' +
        '</div>' +
        '<button type="button" class="lb-sg-cta" data-action="addToCart" disabled>Unavailable in ' + esc(s.wizardColor) + '</button>' +
        '</div></div>';
    }

    var kit = d.kitPricing;
    var leashSection = s.bundle === 'walkkit' ? '<div>' +
      '<div class="lb-sg-included-label">Leash Style</div>' +
      '<div class="lb-sg-leash-list">' +
      FL.LEASH_STYLE_OPTIONS.map(function (style) {
        return '<button type="button" class="lb-sg-leash-card" data-action="selectLeash" data-style="' + esc(style) + '" data-on="' + (s.leashStyle === style) + '">' +
          '<div class="lb-sg-leash-inner">' +
          '<div class="lb-sg-leash-radio" data-on="' + (s.leashStyle === style) + '"></div>' +
          '<div><div class="lb-sg-leash-name-row"><span class="lb-sg-leash-name">' + esc(FL.LEASH_STYLE_DISPLAY_NAME[style] || style) + '</span><span class="lb-sg-leash-tag">' + esc(FL.LEASH_STYLE_SUBTITLE[style] || '') + '</span></div>' +
          '<p class="lb-sg-leash-desc">' + esc(FL.LEASH_STYLE_DESCRIPTIONS[style] || '') + '</p></div>' +
          '</div></button>';
      }).join('') +
      '</div></div>' : '';

    var ctaDisabled = s.cartStatus === 'adding';
    var ctaText = s.cartStatus === 'added' ? 'Added ✓' : (s.cartStatus === 'adding' ? 'Adding…' : esc(d.ctaLabel));
    // Icon path data below is copied verbatim from the live theme's own
    // snippets/icon-*.liquid (icon-cart, icon-star, icon-lock-on,
    // icon-protect) — same solid fill-currentColor style the rest of the
    // theme uses, rather than this component's own previous stroke-based
    // line-art, which didn't match the theme's icon language at all.
    var cartIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style="margin-right:8px;vertical-align:-3px;" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M1.92017 7H22.0799L20.9261 22H3.07401L1.92017 7ZM4.07992 9L4.92607 20H19.074L19.9202 9H4.07992Z"/><path fill-rule="evenodd" clip-rule="evenodd" d="M12 4C10.067 4 8.5 5.567 8.5 7.5H6.5C6.5 4.46243 8.96243 2 12 2C15.0376 2 17.5 4.46243 17.5 7.5H15.5C15.5 5.567 13.933 4 12 4Z"/><path fill-rule="evenodd" clip-rule="evenodd" d="M6.5 11L6.5 8L8.5 8L8.5 11L6.5 11Z"/><path fill-rule="evenodd" clip-rule="evenodd" d="M15.5 11L15.5 8L17.5 8L17.5 11L15.5 11Z"/></svg>';

    // Star row is a real rendering of this.reviewRating (from Judge.me's
    // own metafields, see the Liquid section) — filled-star count is
    // Math.round(rating), never a flat/decorative 5, so it can't overstate
    // a real 3-star average as a full row.
    var starIcon = '<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M15.2493 8.58359L12.0001 2L8.75089 8.58359L1.48547 9.63931L6.74278 14.7639L5.5017 22L12.0001 18.5836L18.4985 22L17.2574 14.7639L22.5147 9.63931L15.2493 8.58359ZM18.195 11.0429L13.9144 10.4209L12.0001 6.54199L10.0858 10.4209L5.80517 11.0429L8.90263 14.0621L8.17142 18.3254L12.0001 16.3126L15.8288 18.3254L15.0975 14.0621L18.195 11.0429Z"/></svg>';
    var reviewLine = this.reviewRating != null
      ? '<div class="lb-sg-trust-stars">' +
        [0, 1, 2, 3, 4].map(function (i) { return '<span data-filled="' + (i < Math.round(this.reviewRating)) + '">' + starIcon + '</span>'; }, this).join('') +
        '<span class="lb-sg-trust-stars-text"><strong>' + this.reviewRating.toFixed(1) + '</strong> (' + this.reviewCount + '+ reviews)</span>' +
        '</div>'
      : '';
    var secureIcon = '<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.5 5H15.5V7H17.5V3H6.5V9H2V21H22V9H8.5V5ZM6.5 11H8.5H20V19H4V11H6.5ZM13.5 14.5C13.5 15.0363 13.2186 15.5068 12.7953 15.772L13 17H11L11.2047 15.772C10.7814 15.5068 10.5 15.0363 10.5 14.5C10.5 13.6716 11.1716 13 12 13C12.8284 13 13.5 13.6716 13.5 14.5Z"/></svg>';
    var secureRow = '<div class="lb-sg-trust-secure">' + secureIcon + '<span>Secure checkout &middot; major cards &amp; digital wallets accepted</span></div>';
    var shieldIcon = '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M21 3.21924L12 0.969238L3 3.21924V19.4141L3.37576 19.7143C4.31332 20.4635 5.81113 21.2736 7.35442 21.8943C8.88712 22.5107 10.6112 23 12 23C13.3888 23 15.1129 22.5107 16.6456 21.8943C18.1889 21.2736 19.6867 20.4635 20.6242 19.7143L21 19.4141V3.21924ZM5 18.4298V4.78079L12 3.03079L19 4.78079V18.4298C18.2408 18.9548 17.1188 19.5483 15.8993 20.0388C14.448 20.6225 13.0064 21 12 21C10.9936 21 9.55204 20.6225 8.1007 20.0388C6.88118 19.5483 5.7592 18.9548 5 18.4298ZM16.3187 8.26794L10.5528 13.6339L7.68126 10.9615L6.31873 12.4256L10.5528 16.366L17.6813 9.73202L16.3187 8.26794Z"/></svg>';
    var leadTimeClause = this.leadTimeText ? ' Ships in ~' + esc(this.leadTimeText) + '.' : '';
    var exchangeDisclosure = this.exchangeDetailsText
      ? ' <button type="button" class="lb-sg-details-link" data-action="toggleExchangeDetails">Details</button>' +
        (s.exchangeDetailsOpen ? '<p class="lb-sg-exchange-details lb-sg-reveal">' + esc(this.exchangeDetailsText) + '</p>' : '')
      : '';

    return '<div class="lb-sg-fit-grid">' +
      leftColumnHtml +
      '<div>' +
      this.collarFamilyPicker(d) +
      colorPickerHtml +
      '<div><div class="lb-sg-included-label">Choose your set</div>' +
      '<div class="lb-sg-tier-list">' +
      // Mirrors the drawer's own upsell-tier markup shape (see
      // lillebjorn-fit-finder.js's tiersHtml/heroEyebrowHtml/priceBadgeHtml)
      // — no visible radio dot, a neutral pill ("STANDARD PRICE") in the
      // same slot a SAVE badge would occupy, price as a right-aligned
      // stacked column. Content (breakdown formula, "+ Free Custom Name
      // Tag") stays exactly what this page already showed; only the visual
      // structure changes to match the drawer's established pattern.
      '<label class="lb-sg-tier" data-on="' + (s.bundle === 'single') + '">' +
      '<input type="radio" name="lb-sg-set" value="single" data-action="selectBundleRadio"' + (s.bundle === 'single' ? ' checked' : '') + '>' +
      '<span class="lb-sg-tier-toprow">' +
        '<span class="lb-sg-tier-name">' + d.standardLabel + '</span>' +
        '<span class="lb-sg-tier-price"><span class="lb-sg-tier-now">' + money(d.standardCents, this.moneyFormat) + '</span><span class="lb-sg-tier-neutraltag">Standard Price</span></span>' +
      '</span>' +
      (d.standardBreakdown ? '<span class="lb-sg-tier-breakdown">' + esc(d.standardBreakdown) + '</span>' : '') +
      '</label>' +
      (kit ? '<label class="lb-sg-tier lb-sg-tier--hero" data-on="' + (s.bundle === 'walkkit') + '">' +
        '<span class="lb-sg-hero-eyebrow">★ Best Value</span>' +
        '<input type="radio" name="lb-sg-set" value="walkkit" data-action="selectBundleRadio"' + (s.bundle === 'walkkit' ? ' checked' : '') + '>' +
        '<span class="lb-sg-tier-toprow">' +
          '<span class="lb-sg-tier-name">' + esc(d.kitLabel) + '</span>' +
          '<span class="lb-sg-tier-price"><span class="lb-sg-price-compare">' + money(kit.setCents, this.moneyFormat) + '</span><span class="lb-sg-tier-now">' + money(kit.discountedCents, this.moneyFormat) + '</span><span class="lb-sg-tier-savebadge">' + kit.badge + '</span></span>' +
        '</span>' +
        '<span class="lb-sg-tier-breakdown">' + esc(d.kitBreakdown) + '</span>' +
        (d.scenarioBoth ? '<span class="lb-sg-tier-extra">+ Free Custom Name Tag</span>' : '') +
        '</label>' : '') +
      '</div></div>' +
      leashSection +
      '<button type="button" class="lb-sg-cta" data-action="addToCart"' + (ctaDisabled ? ' disabled' : '') + '>' + cartIcon + ctaText + '</button>' +
      (s.cartStatus === 'error' ? '<p class="lb-sg-guardrail" data-tone="red" style="margin-top:12px;">Something went wrong adding this to your cart. Please try again.</p>' : '') +
      secureRow +
      '<div class="lb-sg-guarantee-badge">' + shieldIcon +
      '<p class="lb-sg-guarantee"><strong>14-day size exchange.</strong>' + leadTimeClause + exchangeDisclosure + '</p>' +
      '</div>' +
      reviewLine +
      '</div></div>';
  };

  // Floating bar, all devices (see .lb-sg-sticky-bar in the CSS — hidden
  // by default, only ever shown once the main CTA button above has
  // scrolled out of view; see _setupStickyObserver, wired up in render()).
  // The IntersectionObserver it's driven by has no width gate, so this is
  // desktop/tablet/mobile alike — a shopper can add to cart from any
  // scroll position on any device. Rendered at the top level of
  // template(), not nested inside the two-column fit grid, so
  // position:fixed isn't at the mercy of an ancestor's own layout. Reuses
  // the same cart-status states and the same data-action="addToCart" as
  // the main CTA, so the existing delegated click handler picks it up
  // with no new wiring — only the button's own label is shortened since
  // price is already shown separately in the recap next to it.
  proto.stickyBar = function (d) {
    if (d.isMeasurementUnset || !d.isPriceAvailable) return '';
    var s = this.state;
    var ctaDisabled = s.cartStatus === 'adding';
    var ctaText = s.cartStatus === 'added' ? 'Added ✓' : (s.cartStatus === 'adding' ? 'Adding…' : 'Add to Cart');
    var recapSize = d.scenarioBoth
      ? ('Collar ' + d.finalCollarSize + ' · Harness ' + d.finalHarnessSize)
      : ('Size ' + (d.scenarioCollar ? d.finalCollarSize : d.finalHarnessSize));
    var recapText = (s.breedName ? s.breedName + ' · ' : '') + recapSize;
    // Seeded from the last real observer reading (persisted on `this`,
    // survives the innerHTML replace), not hardcoded false — every
    // unrelated state change (color, leash style, anything) re-renders
    // this element as a brand-new DOM node via render()'s innerHTML swap,
    // and IntersectionObserver's own callback is async, so hardcoding
    // false here caused a real, visible flicker: a shopper scrolled down
    // with the bar legitimately visible would see it flash hidden on
    // every single interaction until the freshly (re)attached observer's
    // next callback fired and set it back.
    return '<div class="lb-sg-sticky-bar" data-visible="' + !!this._stickyBarVisible + '">' +
      '<div class="lb-sg-sticky-inner">' +
      '<div class="lb-sg-sticky-info">' +
      '<span class="lb-sg-sticky-recap">' + esc(recapText) + '</span>' +
      '<span class="lb-sg-sticky-price">' + money(d.chosenCents, this.moneyFormat) + '</span>' +
      '</div>' +
      '<button type="button" class="lb-sg-sticky-cta" data-action="addToCart"' + (ctaDisabled ? ' disabled' : '') + '>' + ctaText + '</button>' +
      '</div>' +
      '</div>';
  };

  // Same visual language as the drawer's STEP N OF 4 label + segmented
  // bar, re-derived from this page's own already-computed resolved-state
  // flags rather than a parallel wizard-screen counter — this page has no
  // literal screens to count, just measure -> confirm size -> ready.
  proto.progressIndicator = function (d) {
    var stage = d.isMeasurementUnset ? 0 : (d.isSizeResolved ? 2 : 1);
    var labels = ['Measure', 'Confirm Size', 'Ready to Add'];
    var dots = [0, 1, 2].map(function (i) {
      return '<div class="lb-sg-progress-dot" data-filled="' + (i <= stage) + '"></div>';
    }).join('');
    return '<div class="lb-sg-progress">' +
      '<div class="lb-sg-progress-label">Step ' + (stage + 1) + ' of 3 &middot; ' + labels[stage] + '</div>' +
      '<div class="lb-sg-progress-dots">' + dots + '</div>' +
      '</div>';
  };

  // Three widths plotted on one continuum rather than three boxed cards —
  // position is proportional to the real cm value (mapped across a
  // 1.5–5cm domain), not evenly spaced, so 2/3/4.5 visually reads as the
  // unequal jump it actually is. No illustration: typography, the dot,
  // and the line carry the whole section (see CONCEPT-B-DISCARDED note
  // in the CSS for why the earlier pressure-diagram version is gone).
  proto.widthSpectrum = function () {
    return '<div class="lb-sg-spectrum"><div class="lb-sg-spectrum-rail">' +
      '<div class="lb-sg-spectrum-line"></div>' +
      WIDTHS.map(function (w) {
        var cmNum = w.mm / 10;
        var pos = 10 + (cmNum - 1.5) / 3.5 * 80;
        return '<div class="lb-sg-spectrum-stop" style="left:' + pos + '%;">' +
          '<span class="lb-sg-spectrum-dot"></span>' +
          '<div class="lb-sg-spectrum-spec"><span class="lb-sg-spectrum-cm">' + esc(w.cm) + '</span><span class="lb-sg-spectrum-in">' + esc(w.inches) + '</span></div>' +
          '<div class="lb-sg-spectrum-headline">' + esc(w.headline) + '</div>' +
          '<p class="lb-sg-spectrum-desc">' + esc(w.desc) + '</p>' +
          '<span class="lb-sg-spectrum-tag">' + esc(w.weightRange) + '</span>' +
          '</div>';
      }).join('') +
      '</div></div>';
  };

  proto.template = function () {
    var s = this.state;
    var d = this.deriveVals();
    // Reads the derived values this render already computed; fires only on
    // a genuine forward stage change, so it's a no-op on the vast majority
    // of renders (see _trackProgress).
    this._trackProgress(d);
    var supportEmailHref = 'mailto:' + esc(this.supportEmail);
    var showSharedControlsOnce = s.view === 'all';
    return '<section class="lb-sg-hero">' +
      '<div class="lb-sg-hero__eyebrow">We assist you in choosing the ideal size for your dog</div>' +
      '<h1 class="lb-sg-hero__title">Size Guide &amp; Fit Finder</h1>' +
      '<p class="lb-sg-hero__copy">Enter your dog\'s measurements below and we\'ll match the size for you — right on this page. For personalized help, reach out to <a href="' + supportEmailHref + '">' + esc(this.supportEmail) + '</a>.</p>' +
      '<div class="lb-sg-viewtabs"><div class="lb-sg-viewtabs__indicator" style="transform:translateX(' + (s.view === 'collar' ? '0' : s.view === 'harness' ? '100%' : '200%') + ');"></div>' +
      '<button class="lb-sg-viewtab" data-action="selectView" data-view="collar" data-on="' + (s.view === 'collar') + '">Collar</button>' +
      '<button class="lb-sg-viewtab" data-action="selectView" data-view="harness" data-on="' + (s.view === 'harness') + '">Harness</button>' +
      '<button class="lb-sg-viewtab" data-action="selectView" data-view="all" data-on="' + (s.view === 'all') + '">View All</button>' +
      '</div></section>' +
      '<section class="lb-sg-section"><div class="lb-sg-card">' +
      this.progressIndicator(d) +
      (showSharedControlsOnce ? this.sharedControlsPanel() : '') +
      '<div class="' + (this._justSwitchedView ? 'lb-sg-reveal' : '') + '">' +
      this.collarSection(d, !showSharedControlsOnce) + this.harnessSection(d, !showSharedControlsOnce) +
      '</div>' +
      '<div id="fit-result" class="lb-sg-result">' + this.resultBox(d) + this.fitDetailPanel(d) + '</div>' +
      '</div></section>' +
      // The standalone "Why strap width matters" section (widthSpectrum())
      // used to render here as its own <section>. Removed — the same
      // 2/3/4.5cm comparison now lives inline in the collar measure block
      // (see the "Collar only" disclosure above in measureBlock), and
      // showing both was the exact same copy twice on one page. The
      // function and its CSS are left in place, unused, rather than
      // deleted outright — this project has no git history to recover
      // from, and this exact section has changed direction several times
      // in one sitting.
      this.stickyBar(d);
  };

  proto.render = function () {
    // Tracked so template() can fade in ONLY the render immediately after
    // a tab actually switches (view or collarTab) — every other state
    // change (typing a measurement, picking a color, anything) also goes
    // through this same full re-render, and would re-trigger the fade
    // every time if these flags weren't scoped to a genuine change.
    this._justSwitchedView = this._lastView != null && this._lastView !== this.state.view;
    this._justSwitchedCollarTab = this._lastCollarTab != null && this._lastCollarTab !== this.state.collarTab;
    this._lastView = this.state.view;
    this._lastCollarTab = this.state.collarTab;
    // Replacing innerHTML destroys the currently-focused element, which
    // makes the browser fire a synchronous, synthetic blur/focusout on it
    // before this line even returns — not a real "the user looked away"
    // blur. _isRerendering lets the focusout listener below tell the two
    // apart, so re-rendering while a shopper is mid-type never clears
    // _activeFocusId or closes the breed dropdown out from under them.
    this._isRerendering = true;
    this.innerHTML = this.template();
    this._isRerendering = false;
    var activeFocusId = this._activeFocusId;
    if (activeFocusId) {
      var el = this.querySelector('[data-input="' + activeFocusId + '"]');
      if (el) {
        el.focus();
        if (el.setSelectionRange && el.type !== 'number') {
          try { el.setSelectionRange(el.value.length, el.value.length); } catch (e) {}
        }
      }
    }
    // Fade+slide the freshly-rendered advisory blocks (guardrail/boundary/
    // custom-size) in, one frame after insertion, rather than having them
    // snap into place — see .lb-sg-reveal / .lb-sg-reveal--in in the CSS.
    var toReveal = this.querySelectorAll('.lb-sg-reveal:not(.lb-sg-reveal--in)');
    if (toReveal.length) {
      requestAnimationFrame(function () {
        toReveal.forEach(function (el) { el.classList.add('lb-sg-reveal--in'); });
      });
    }
    this._setupStickyObserver();
    this._positionBreedsPopover();
  };

  // .lb-sg-breeds-popover is position:fixed (see the CSS comment on that
  // rule for why) so it needs its own top/left computed from the trigger
  // button's real on-screen position — CSS alone can't anchor a fixed box
  // to a specific element the way `top: calc(100% + 6px)` anchors an
  // absolute one. Runs after every render() while a popover is open;
  // no-ops instantly otherwise.
  proto._positionBreedsPopover = function () {
    if (!this.state.openBreedsPopover) return;
    var trigger = this.querySelector('[data-popover="' + this.state.openBreedsPopover + '"]');
    var popover = this.querySelector('.lb-sg-breeds-popover');
    if (!trigger || !popover) return;
    var tRect = trigger.getBoundingClientRect();
    var pRect = popover.getBoundingClientRect();
    var margin = 8;
    var left = tRect.left;
    left = Math.min(left, window.innerWidth - pRect.width - margin);
    left = Math.max(left, margin);
    var top = tRect.bottom + 6;
    // Flip above the trigger if there isn't room below — keeps the
    // popover fully on-screen (and legible) near the bottom of a long
    // page instead of running off under the fold.
    if (top + pRect.height > window.innerHeight - margin) {
      top = tRect.top - pRect.height - 6;
    }
    popover.style.left = left + 'px';
    popover.style.top = top + 'px';
  };

  // Every render replaces this.innerHTML wholesale, so both the main CTA
  // and the sticky bar are fresh DOM nodes each time — any observer from
  // the previous render is watching now-detached elements and must be
  // torn down and re-attached, not reused. Visibility itself is plain
  // "is the main CTA on screen or not"; the <=680px gate lives in CSS
  // (.lb-sg-sticky-bar only ever applies display:flex inside that media
  // query), so this never has to check viewport width itself.
  proto._setupStickyObserver = function () {
    var self = this;
    if (this._stickyObserver) this._stickyObserver.disconnect();
    var mainCta = this.querySelector('.lb-sg-cta');
    var stickyBar = this.querySelector('.lb-sg-sticky-bar');
    if (!mainCta || !stickyBar) {
      // Nothing eligible to be sticky right now (no measurement yet, no
      // price available, etc.) — reset the persisted reading so if a
      // sticky bar DOES render later (a fresh, unrelated state), it starts
      // at its correct default (hidden) instead of carrying over a stale
      // "true" from whatever the shopper's scroll position happened to be
      // the last time one existed.
      this._stickyBarVisible = false;
      return;
    }
    this._stickyObserver = new IntersectionObserver(function (entries) {
      self._stickyBarVisible = !entries[0].isIntersecting;
      stickyBar.setAttribute('data-visible', String(self._stickyBarVisible));
    }, { threshold: 0 });
    this._stickyObserver.observe(mainCta);
  };

  // The drawer fires sizing_tool_opened on an actual open click. This page
  // has no open action — it IS the page — so the comparable funnel entry is
  // the guide scrolling into view: a shopper who lands on the page and
  // never scrolls down to the guide has not "opened" it in any sense that
  // should count against the drawer's numbers. Fires once per page load and
  // then disconnects; this is a funnel entry, not a visibility counter.
  proto._setupViewObserver = function () {
    var self = this;
    var fire = function () {
      trackSizingEvent("sizing_tool_opened", { preselect_option: self.state.view });
    };
    if (typeof IntersectionObserver !== "function") { fire(); return; }
    // Contained for the same reason as _trackProgress: this is called at the
    // tail of connectedCallback, and a throw here would abort the rest of
    // element setup on a live storefront.
    try {
      self._observeForEntry(fire);
    } catch (e) {
      console.warn("lillebjorn-size-guide: view observer setup failed", e);
    }
  };

  proto._observeForEntry = function (fire) {
    var self = this;
    this._viewObserver = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      // Disconnect BEFORE firing — the observer can deliver more than one
      // intersecting entry in a single callback batch, which would
      // otherwise double-count the entry event.
      self._viewObserver.disconnect();
      self._viewObserver = null;
      fire();
      // threshold 0 ("any part of it is on screen"), NOT a ratio like 0.25.
      // intersectionRatio is measured against the element's OWN full height,
      // and this element is the entire guide — ~6800px tall in the test
      // harness. A 0.25 ratio would need a 1700px-tall viewport to ever be
      // satisfied, so on every real screen the callback would simply never
      // run and this event would silently never fire. Same threshold the
      // sticky-bar observer above uses, for the same reason.
    }, { threshold: 0 });
    this._viewObserver.observe(this);
  };

  // Mirrors the drawer's "forward progress only" rule, adapted to a page
  // with no wizard screens to count. The drawer can rely on a screen index
  // that only ever moves forward on a Next click; here a shopper can freely
  // clear a measurement and drop back to an earlier stage, so a plain
  // transition check would report one shopper's single session as many
  // completions. The high-water mark makes each stage report at most once
  // per page load. Called from template(), the one place per render where
  // deriveVals() has already been computed — recomputing it here would
  // double the derive cost on every keystroke.
  proto._trackProgress = function (d) {
    try {
      this._trackProgressInner(d);
    } catch (e) {
      // This runs inside template(), i.e. in the render path on every
      // keystroke. trackSizingEvent guards its own transmission, but a
      // throw anywhere in the stage arithmetic here would propagate out of
      // template() and leave the guide rendering nothing at all. No
      // analytics failure is worth taking the page down for, so the whole
      // computation is contained.
      console.warn("lillebjorn-size-guide: progress tracking failed", e);
    }
  };

  proto._trackProgressInner = function (d) {
    var stage = d.isMeasurementUnset ? 0 : (d.isSizeResolved ? 2 : 1);
    if (this._maxStageReached == null) this._maxStageReached = 0;
    if (stage <= this._maxStageReached) return;
    // Steps are reported one at a time even when a single state change
    // jumps two stages (a pasted measurement can resolve a size outright),
    // so step 1 is never missing from the funnel underneath a step 2.
    for (var next = this._maxStageReached + 1; next <= stage; next++) {
      trackSizingEvent("sizing_tool_step_completed", { step_index: next });
      if (next === 2) {
        trackSizingEvent("sizing_tool_result_viewed", {
          recommended_neck_size: d.neckResolved ? d.finalCollarSize : null,
          recommended_chest_size: d.chestResolved ? d.finalHarnessSize : null
        });
      }
    }
    this._maxStageReached = stage;
  };

  /* ---------- Analytics ---------- */
  // Deliberately identical to trackSizingEvent in lillebjorn-fit-finder.js
  // apart from the `tool` value — see that file's much longer comment for
  // why this goes through gtag() rather than dataLayer.push() (short
  // version: this store runs plain gtag.js via Shopify's Google & YouTube
  // channel with NO GTM container, so a plain dataLayer.push({event:...})
  // is shaped for a consumer that does not exist and silently reaches
  // nobody — which is exactly how the drawer's events went unrecorded for
  // 90 days). Both surfaces emit the same sizing_tool_* family separated
  // only by `tool`, so one GA4 report compares them directly.
  var LBSG_GA4_MEASUREMENT_ID = "G-Y1BK52L3YX";

  function trackSizingEvent(name, payload) {
    var params = Object.assign({ tool: "size_guide" }, payload || {});
    try {
      if (typeof window.gtag === "function") {
        window.gtag("event", name, Object.assign({ send_to: LBSG_GA4_MEASUREMENT_ID }, params));
        return;
      }
      if (window.dataLayer && typeof window.dataLayer.push === "function") {
        window.dataLayer.push(Object.assign({ event: name }, params));
      }
    } catch (e) {
      // A broken or blocked analytics tag must never affect the guide itself.
      console.warn("lillebjorn-size-guide: analytics event failed", e);
    }
  }

  /* ---------- Add to cart ---------- */
  // The cart drawer's runtime section id is only ever reliably known from
  // the DOM. Mirrors lillebjorn-fit-finder.js's own resolveCartDrawerSectionId
  // exactly — same theme, same real .cart-drawer__wrapper/shopify-section-*
  // selectors, so both surfaces resolve the identical id.
  function resolveCartDrawerSectionId() {
    var liveDrawer = document.querySelector(".cart-drawer__wrapper");
    if (!liveDrawer) return null;
    var sectionHost = liveDrawer.closest('[id^="shopify-section-"]');
    return sectionHost ? sectionHost.id.replace(/^shopify-section-/, "") : null;
  }

  proto.addToCart = function (items) {
    var self = this;
    if (!items.length) return;
    this.setState({ cartStatus: 'adding' });

    var routes = window.routes || {};
    var cartRoot = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
    var cartAddUrl = routes.cart_add_url || (cartRoot + "cart/add.js");

    // Requesting the drawer's section markup INLINE, as part of this same
    // POST — mirrors fit-finder's addToCart exactly. This theme's cart
    // drawer lives inside a section-group header (confirmed by its own
    // runtime id), where the standalone Section Rendering API doesn't
    // reliably respond, but this inline request path does.
    var drawerSectionId = resolveCartDrawerSectionId();
    var payload = { items: items.map(function (id) { return { id: id, quantity: 1 }; }) };
    if (drawerSectionId) {
      payload.sections = [drawerSectionId];
      payload.sections_url = window.location.pathname;
    }

    fetch(cartAddUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (err) { throw err; });
        return res.json();
      })
      .then(function (response) {
        self.setState({ cartStatus: 'added' });
        var item = (response && response.items && response.items[0]) || response;
        document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true, detail: { item: item } }));
        document.dispatchEvent(new CustomEvent("cart:build", { bubbles: true, detail: { item: item } }));
        document.documentElement.dispatchEvent(new CustomEvent("CartUpdate", { bubbles: true, detail: { item: item } }));
        self.dispatchEvent(new CustomEvent("lillebjorn:added-to-cart", { bubbles: true, detail: { item: item } }));
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === "function") {
          var publishedLines = (response && response.items) || [item];
          publishedLines.forEach(function (line) {
            window.Shopify.analytics.publish("product_added_to_cart", { cartLine: line });
          });
        }

        // GA4's own recommended add_to_cart shape (items[] etc.), so
        // this lights up GA4's built-in Ecommerce reports rather than
        // arriving as an opaque custom event. Mirrors the drawer's payload
        // field for field — see lillebjorn-fit-finder.js's addToCart.
        // Computed from response.items rather than reusing publishedLines
        // above, since that variable only exists inside the
        // Shopify.analytics-present branch.
        var gaLines = (response && response.items) || [item];
        var gaItems = gaLines.map(function (line) {
          return {
            item_id: String(line.variant_id != null ? line.variant_id : line.id),
            item_name: line.product_title || line.title || "",
            item_variant: line.variant_title || undefined,
            price: (line.price || 0) / 100,
            quantity: line.quantity || 1
          };
        });
        // A Walk Kit / bundle add carries 2-3 real lines, so value sums
        // every line rather than reading the first one's price — the same
        // undercount the publish loop above exists to avoid.
        var gaValue = gaLines.reduce(function (sum, line) {
          var lineTotal = line.final_line_price != null ? line.final_line_price
            : line.line_price != null ? line.line_price
            : (line.price || 0) * (line.quantity || 1);
          return sum + lineTotal / 100;
        }, 0);
        // currency/value/items sit at the top level of the params: gtag()
        // only reads them there. Nested under `ecommerce: {…}` (the GTM
        // dataLayer shape) gtag ignored them and GA4 logged value 0.
        trackSizingEvent("sizing_tool_add_to_cart", {
          // Shopify's AJAX cart response carries no ISO currency code;
          // Shopify.currency.active is the standard global every
          // storefront sets. moneyFormat is a display template
          // ("€{{amount}}"), not a currency code, so it isn't usable here.
          currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || undefined,
          value: gaValue,
          items: gaItems,
          tier: self.state.bundle
        });

        // Best-effort only: lillebjorn-fit-finder.js's own comments confirm
        // IT doesn't force-open anything either (see "this is shown
        // regardless of whether the theme's own drawer opens" — the exact
        // reason it also shows its own View Cart fallback link). Every
        // event/mechanism that file DOES use is already mirrored above and
        // below verbatim, so whatever actually triggers this theme's
        // native "Product added" toast lives in code outside both files
        // (its own bundle.js) that isn't available here. window.shtCartDrawer
        // is confirmed to exist as this theme's real drawer controller (see
        // fit-finder's own comment on its close-button wiring) — .open() is
        // an idiomatic guess at its public API paired with its .close(),
        // not a confirmed one, so this is wrapped defensively: if the guess
        // is wrong, it silently no-ops rather than breaking the cart flow.
        try {
          if (window.shtCartDrawer && typeof window.shtCartDrawer.open === "function") {
            window.shtCartDrawer.open();
          } else {
            var drawerHost = document.querySelector("sht-cart-drwr");
            if (drawerHost && !drawerHost.hasAttribute("open")) drawerHost.setAttribute("open", "");
          }
        } catch (openErr) {
          console.warn("lillebjorn-size-guide: best-effort cart drawer open failed", openErr);
        }

        // The events above only help if this theme's own bundle.js happens
        // to be listening for one of them — confirmed NOT sufficient on
        // this storefront (see lillebjorn-fit-finder.js's identical fix):
        // the header cart badge and the drawer's own markup are patched
        // directly below, independent of whatever event contract this
        // theme's JS actually uses.
        var cartJsUrl = (routes.cart_url || (cartRoot + "cart")) + ".js";
        var cartCountPromise = fetch(cartJsUrl, { headers: { Accept: "application/json" } })
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (cart) {
            if (!cart) return;
            self.refreshHeaderCartCount(cart);
            document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true, detail: { cart: cart } }));
          })
          .catch(function (cartErr) {
            console.warn("lillebjorn-size-guide: could not refresh header cart count", cartErr);
          });

        var inlineSectionHtml = drawerSectionId && response && response.sections && response.sections[drawerSectionId];
        var drawerRefreshPromise = self.refreshCartDrawerMarkup(inlineSectionHtml)
          .catch(function (drawerErr) {
            console.warn("lillebjorn-size-guide: could not refresh cart drawer markup", drawerErr);
          });

        setTimeout(function () {
          if (self.state.cartStatus === 'added') self.setState({ cartStatus: null });
        }, 2200);

        return Promise.all([cartCountPromise, drawerRefreshPromise]);
      })
      .catch(function (err) {
        console.error("lillebjorn-size-guide: /cart/add.js failed", err);
        self.setState({ cartStatus: 'error' });
      });
  };

  // Direct DOM patch for this storefront's real header cart badge. Mirrors
  // lillebjorn-fit-finder.js's refreshHeaderCartCount exactly — same
  // theme, same real selectors (.header__cart-count / .cart-count-number /
  // header-cart-count-N) — see that file's own comment for why dispatched
  // events alone can't be relied on to update this badge.
  proto.refreshHeaderCartCount = function (cart) {
    document.querySelectorAll(".header__cart-count").forEach(function (badge) {
      var numberEl = badge.querySelector(".cart-count-number");
      if (numberEl) numberEl.textContent = cart.item_count;
      var isEmpty = cart.item_count === 0;
      badge.classList.toggle("opacity-0", isEmpty);
      badge.classList.toggle("hidden-xs", isEmpty);
      badge.className = badge.className.replace(/header-cart-count-\d+/, "header-cart-count-" + cart.item_count);
      var animateHost = badge.closest("a, button") || badge.parentElement;
      if (animateHost) {
        animateHost.classList.add("header-cart-status--animate");
        setTimeout(function () { animateHost.classList.remove("header-cart-status--animate"); }, 400);
      }
    });
  };

  // Swaps freshly-rendered markup into the live, currently-stale
  // .cart-drawer__wrapper element. Mirrors lillebjorn-fit-finder.js's
  // refreshCartDrawerMarkup exactly — same theme, same real selectors,
  // same Section Rendering API -> full-page-refetch fallback chain. See
  // that file's own extensive comments for why each step exists; not
  // re-duplicated here to avoid the two copies drifting out of sync in
  // wording while diverging in behavior.
  proto.refreshCartDrawerMarkup = function (preFetchedHtml) {
    var liveDrawer = document.querySelector(".cart-drawer__wrapper");
    if (!liveDrawer) return Promise.resolve();

    var self = this;
    this._cartDrawerRequestId = (this._cartDrawerRequestId || 0) + 1;
    var myRequestId = this._cartDrawerRequestId;

    var routes = window.routes || {};
    var cartUrl = routes.cart_url || "/cart";
    var sectionId = resolveCartDrawerSectionId();

    function isStale() { return myRequestId !== self._cartDrawerRequestId; }

    function applyFreshDrawer(html) {
      if (isStale()) return false;
      var freshDoc = new DOMParser().parseFromString(html, "text/html");
      var freshDrawer = freshDoc.querySelector(".cart-drawer__wrapper");
      if (!freshDrawer) return false;
      liveDrawer.innerHTML = freshDrawer.innerHTML;
      return true;
    }

    function fallbackToFullPageFetch() {
      if (isStale()) return Promise.resolve();
      return fetch(window.location.href, { headers: { "X-Requested-With": "XMLHttpRequest" } })
        .then(function (res) { return res.ok ? res.text() : null; })
        .then(function (html) { if (html) applyFreshDrawer(html); });
    }

    if (preFetchedHtml && applyFreshDrawer(preFetchedHtml)) return Promise.resolve();
    if (!sectionId) return fallbackToFullPageFetch();

    return fetch(cartUrl + "?sections=" + sectionId, { headers: { Accept: "application/json" } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (sections) {
        if (isStale()) return;
        var sectionHtml = sections && sections[sectionId];
        if (sectionHtml && applyFreshDrawer(sectionHtml)) return;
        return fallbackToFullPageFetch();
      })
      .catch(function () {
        return fallbackToFullPageFetch();
      });
  };

  /* ---------- Debounced measurement input ---------- */
  // Only the two number inputs debounce (~400ms after the last keystroke)
  // — a boundary tie / guardrail / custom-size block popping in on every
  // intermediate digit while typing "42" (first "4", then "42") read as
  // jumpy and startling. Steppers and breed-driven auto-fill stay
  // instant — those are already single, deliberate actions, not typing.
  proto._applyMeasurementInput = function (key, rawValue, opts) {
    opts = opts || {};
    var s = this.state;
    var min = key === 'neck' ? FL.NECK_NODES[0] : FL.CHEST_NODES[0];
    var max = key === 'neck' ? FL.NECK_CUSTOM_MAX : FL.CHEST_CUSTOM_MAX;
    if (rawValue.trim() === '') { var p = {}; p[key] = null; p[key + 'BoundaryChoice'] = null; this.setState(p); return; }
    // European/Russian keyboards submit a decimal comma ("10,5") — normalize
    // to a dot before parsing so it doesn't read as an invalid number.
    var val = Number(rawValue.replace(',', '.'));
    // Incomplete/invalid while still typing (e.g. "-", "."): wait for a
    // parseable number rather than forcing it to 0 and clamping to the
    // floor size mid-keystroke. On commit (blur — opts.commit, see the
    // focusout handler), there's no more typing to wait for: the field's
    // raw-text buffer is already cleared by then (see measureBlock), so an
    // unresolved invalid value would otherwise sit there indefinitely with
    // nothing to re-render it back to the last valid value. state itself
    // never changed (we return before touching it either way), so a plain
    // render() — not setState — is enough to resync the DOM to it.
    if (isNaN(val)) { if (opts.commit) this.render(); return; }
    var cm = s.unit === 'cm' ? val : inToCm(val);
    // Any new reading re-opens the boundary question — a stale loose/snug
    // pick from a previous value must never silently carry over onto a
    // different measurement (matches lillebjorn-fit-finder.js's commit()).
    var patch = {}; patch[key] = FL.clamp(cm, min, max); patch[key + 'BoundaryChoice'] = null;
    this.setState(patch);
  };

  // document-scoped listeners (outside-click closes the breed dropdown/
  // breeds popover; scroll closes the breeds popover) — kept separate from
  // the element-scoped delegation in _wireEvents below, and idempotent
  // (guarded by _docListenersWired) so connectedCallback can safely call
  // this on every connect, not just the first, without double-attaching.
  // References are stored on `this` (not anonymous closures) specifically
  // so disconnectedCallback can remove them again.
  proto._wireDocumentListeners = function () {
    if (this._docListenersWired) return;
    this._docListenersWired = true;
    var self = this;

    this._onDocClick = function (e) {
      if (self.state.showBreedList && !e.target.closest('.lb-sg-autocomplete')) self.closeBreedListWithGrace();
      if (self.state.openBreedsPopover && !e.target.closest('.lb-sg-breeds-more-wrap')) self.setState({ openBreedsPopover: null });
    };
    document.addEventListener('click', this._onDocClick);

    // A position:fixed popover doesn't move with the page (see the CSS
    // comment on .lb-sg-breeds-popover) — its top/left are computed once,
    // relative to the viewport, right after it opens. Left alone, scrolling
    // the page (or a nested scroll container, hence capture:true to catch
    // that too) would visually detach it from the "+more" trigger it's
    // supposed to be anchored to. Closing on scroll is the same tradeoff
    // most popover/dropdown implementations make, rather than paying for a
    // scroll listener that repositions on every frame for a rarely-open,
    // rarely-scrolled-mid-open element.
    this._onDocScroll = function () {
      if (self.state.openBreedsPopover) self.setState({ openBreedsPopover: null });
    };
    document.addEventListener('scroll', this._onDocScroll, { passive: true, capture: true });
  };

  /* ---------- Event wiring (delegated, bound once in connectedCallback) ---------- */
  proto._wireEvents = function () {
    var self = this;

    this.addEventListener('focusin', function (e) {
      var t = e.target.closest('[data-input]');
      self._activeFocusId = t ? t.dataset.input : null;
    });

    // Clicking a breed in the dropdown is a mousedown (focus/blur fires)
    // THEN a click (the actual pickBreed action fires here on the 'click'
    // listener below). Without this, that mousedown blurs the breed input,
    // which synchronously closes the dropdown (see the focusout listener
    // below) and replaces the innerHTML mid-click — so the button the
    // shopper is clicking gets removed from the DOM before its own click
    // event finishes, and the click never reaches the pickBreed handler.
    // preventDefault on mousedown stops the browser from shifting focus at
    // all, so the input never blurs and the click lands normally.
    this.addEventListener('mousedown', function (e) {
      if (e.target.closest('.lb-sg-autocomplete-list')) e.preventDefault();
    });

    this.addEventListener('click', function (e) {
      // Open the breed dropdown on click, plain and simple — also covers
      // clicking back into the field while a previous close is still
      // fading out (breedListClosing), which snaps it back to fully open.
      if (e.target.closest('[data-input="breed"]') && (!self.state.showBreedList || self.state.breedListClosing)) {
        clearTimeout(self._breedListCloseTimer);
        self.setState({ showBreedList: true, breedListClosing: false });
      }
      var t = e.target.closest('[data-action]');
      if (!t) return;
      var s = self.state;
      switch (t.dataset.action) {
        case 'selectView':
          self.setState({ view: t.dataset.view });
          break;
        case 'setUnit': self.setState({ unit: t.dataset.unit }); break;
        case 'selectCollarTab': self.setState({ collarTab: t.dataset.tab }); break;
        case 'setAge': {
          var profile = self.currentBreedProfile();
          var sizes = FL.applyAgeProfile(profile, t.dataset.age);
          self.setState({ ageProfile: t.dataset.age, neck: s.neck !== null ? sizes.neckSize : null, chest: s.chest !== null ? sizes.chestSize : null, neckBoundaryChoice: null, chestBoundaryChoice: null });
          break;
        }
        case 'pickBreed': self.applyBreedSelection(t.dataset.name); break;
        case 'stepNeck': case 'stepChest': {
          var isNeck = t.dataset.action === 'stepNeck';
          var key = isNeck ? 'neck' : 'chest';
          var min = isNeck ? FL.NECK_NODES[0] : FL.CHEST_NODES[0];
          var max = isNeck ? FL.NECK_CUSTOM_MAX : FL.CHEST_CUSTOM_MAX;
          var dir = t.dataset.dir === 'inc' ? 1 : -1;
          var nextCm;
          if (s.unit === 'cm') {
            // Unchanged: step the canonical cm value by a clean 1cm.
            var curCm = s[key] !== null ? s[key] : Number(t.dataset.fallback);
            nextCm = curCm + dir;
          } else {
            // Step the DISPLAYED inch value by a clean 0.25in per click —
            // US shoppers read boundaries in quarter-inches natively, and a
            // 0.25in step still lands exactly on table boundaries like
            // 12.5in/12.75in without the click fatigue of a finer step.
            // Done in integer QUARTERS-of-an-inch (48, 49, 50…), not
            // floating decimals: quarters are a power-of-two fraction
            // (1/4), so curIn*4 and quarters/4 are exact in binary
            // floating point — no rounding drift over repeated clicks,
            // unlike tenths (0.1 has no exact binary representation). The
            // result is converted back to cm at FULL precision (no
            // Math.round to a whole cm) — sizeFromValue/boundaryTie in
            // lillebjorn-fit-logic.js are plain numeric range checks
            // (`cm < nodes[i+1]`, `Math.abs(cm - n)`), not integer-cm-
            // dependent, so they match correctly against the exact value
            // with no loss of accuracy.
            var curIn = s[key] !== null ? cmToIn(s[key]) : Number(t.dataset.fallback);
            var nextQuarters = Math.round(curIn * 4) + dir;
            nextCm = (nextQuarters / 4) * 2.54;
          }
          // A stepper click is a deliberate, discrete action (not typing),
          // so it should always show the clean stepped result — drop any
          // in-progress free-typed buffer for this field.
          self._rawMeasureText[key] = null;
          var patch = {}; patch[key] = FL.clamp(nextCm, min, max); patch[key + 'BoundaryChoice'] = null;
          self.setState(patch);
          break;
        }
        case 'setBoundary': {
          var patch2 = {}; patch2[t.dataset.key + 'BoundaryChoice'] = t.dataset.pref;
          self.setState(patch2);
          break;
        }
        case 'selectColor': self.setState({ wizardColor: t.dataset.color }); break;
        case 'selectCollarFamily': self.setState({ selectedCollarFamily: t.dataset.family }); break;
        case 'selectLeash': self.setState({ leashStyle: t.dataset.style }); break;
        case 'toggleExchangeDetails': self.setState({ exchangeDetailsOpen: !s.exchangeDetailsOpen }); break;
        case 'toggleBreedsPopover': self.setState({ openBreedsPopover: s.openBreedsPopover === t.dataset.popover ? null : t.dataset.popover }); break;
        case 'toggleMobileRow': {
          // Read the row's own rendered data-open (already resolved at
          // render time — either the default "open because matched" or a
          // previous explicit override) and flip it, rather than
          // recomputing match logic here. Storing the explicit override
          // (not just a plain toggle count) means a shopper closing their
          // own matched row, or opening an unmatched one, survives the
          // next re-render instead of snapping back to the default.
          var mrow = t.closest('.lb-sg-mrow');
          var wasOpen = !!mrow && mrow.getAttribute('data-open') === 'true';
          var nextMobileRowOpen = Object.assign({}, s.mobileRowOpen);
          nextMobileRowOpen[t.dataset.key] = !wasOpen;
          self.setState({ mobileRowOpen: nextMobileRowOpen });
          break;
        }
        case 'scrollToFit': {
          var target = self.querySelector('#fit-result');
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          break;
        }
        case 'addToCart': {
          if (s.cartStatus === 'adding') return;
          var d = self.deriveVals();
          self.addToCart(d.cartItems);
          break;
        }
      }
    });

    this.addEventListener('change', function (e) {
      if (e.target.dataset.action === 'selectBundleRadio') self.setState({ bundle: e.target.value });
      if (e.target.dataset.input === 'bodyBuild') {
        // Same smart-switch rule as applyBreedSelection — a manual Body &
        // Neck Build pick resolves to a category exactly the same way
        // (see currentBreedProfile's BUILD_TO_CATEGORY override), so it
        // should sync the Classic/Wide tab identically, not just a typed
        // breed name.
        var pickedBuild = e.target.value;
        self.setState({ bodyBuild: pickedBuild, collarTab: FL.BUILD_TO_CATEGORY[pickedBuild] === 'sighthound' ? 'wide' : 'classic' });
      }
    });

    this.addEventListener('input', function (e) {
      var el = e.target;
      if (el.dataset.input === 'breed') {
        // Typing again cancels any close-fade already in flight (see
        // closeBreedListWithGrace) — otherwise the list would re-render
        // fully visible (showBreedList true) while still carrying the
        // --closing class from an in-progress fade, i.e. present but
        // invisible/unclickable.
        clearTimeout(self._breedListCloseTimer);
        var patch = { breedQuery: el.value, showBreedList: true, breedListClosing: false };
        // Instant reset: clearing the field back to empty must drop any
        // already-committed breed right away, not just visually — the
        // same fix lillebjorn-fit-finder.js's own breed combobox already
        // applies (see its "instant re-lock" comment). Without this,
        // currentBreedProfile() silently keeps resolving the OLD breed
        // (and its category/cross-sells) while the input looks empty.
        //
        // bodyBuild must reset here too, and this page specifically needs
        // it (the drawer doesn't): applyBreedSelection always auto-sets
        // bodyBuild alongside breedName as a visible "suggested build"
        // convenience, and currentBreedProfile() re-applies whatever
        // bodyBuild currently holds as a category override on EVERY
        // render (not just once at pick-time). Left alone, clearing the
        // breed text would still silently keep recommending the OLD
        // breed's category (e.g. still Wide collars for a since-cleared
        // sighthound) via that stale override.
        if (!el.value.trim()) { patch.breedName = ''; patch.bodyBuild = null; }
        self.setState(patch);
      } else if (el.dataset.input === 'neck' || el.dataset.input === 'chest') {
        var key = el.dataset.input;
        // Buffer the literal keystrokes synchronously (no debounce) so
        // measureBlock can redisplay exactly this on the next render — see
        // the _rawMeasureText comment in the constructor. The debounced
        // _applyMeasurementInput call below only drives the recommended
        // size (sizeLabel/guardrail/etc); it never touches el.value itself.
        self._rawMeasureText[key] = el.value;
        var rawValue = el.value;
        clearTimeout(self._debounceTimers[key]);
        self._debounceTimers[key] = setTimeout(function () {
          self._applyMeasurementInput(key, rawValue);
        }, 400);
      }
    });

    this.addEventListener('focusout', function (e) {
      // Ignore the synthetic blur render() itself triggers by replacing
      // innerHTML mid-render (see the _isRerendering comment in render())
      // — only act on a real "the shopper looked away" blur.
      if (self._isRerendering) return;
      var t = e.target.closest && e.target.closest('[data-input]');
      if (!t) return;
      if (self._activeFocusId === t.dataset.input) self._activeFocusId = null;
      // Close on blur only — deliberately does NOT auto-commit whatever's
      // typed as a breed name (unlike the drawer's own combobox), since
      // this page shows the breed search and the neck/chest inputs
      // together on one screen and committing on blur could silently
      // overwrite a measurement the shopper already entered. A breed is
      // only ever committed by an explicit dropdown pick ('pickBreed') or
      // the instant clear-to-empty reset in the 'input' handler below.
      if (t.dataset.input === 'breed') self.closeBreedListWithGrace();
      if (t.dataset.input === 'neck' || t.dataset.input === 'chest') {
        var key = t.dataset.input;
        // Finishing up: flush whatever's pending immediately (don't make
        // them wait out the debounce) and drop the raw-text buffer so this
        // field's next render shows the clean, rounded/formatted value —
        // free-typed precision is preserved while typing, and normalized
        // the moment they're done.
        clearTimeout(self._debounceTimers[key]);
        self._rawMeasureText[key] = null;
        self._applyMeasurementInput(key, t.value, { commit: true });
      }
    });
  };

  customElements.define("lillebjorn-size-guide", LillebjornSizeGuide);
})();
